import type { DeskDailyReportResponse } from '../types/deskReport';
import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';
import type { Employee } from '../types/employee';
import type { 
  LoginRequest, 
  LoginResponse, 
  Company, 
  User,
  CreateUserRequest, 
  CreateUserResponse,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CreateCompanyResponse,
  UsersResponse,
  RolesResponse,
  Role,
  ResourcePermission,
  RolesSchemaResponse,
  DepartmentAssignment,
  EmployeeAssignment,
  CreateUserCompanyAssignmentRequest,
  CreateUserCompanyAssignmentResponse,
  UserCompanyAssignmentResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ChangePasswordRequest
} from '../types/auth';
import type { CamerasResponse, Camera, CreateCameraRequest, CreateCameraResponse, UpdateCameraRequest, UpdateCameraResponse } from '../types/camera';
import type { 
  EmployeesResponse, 
  CreateEmployeeRequest, 
  CreateEmployeeResponse, 
  UpdateEmployeeRequest, 
  UpdateEmployeeResponse,
  BulkImportRequest,
  BulkImportResponse,
  EmployeeImagesResponse,
  EmployeeImageResponse,
  EmployeeImageDeleteResponse
} from '../types/employee';
import type { DepartmentsResponse, CreateDepartmentRequest, CreateDepartmentResponse, UpdateDepartmentRequest, UpdateDepartmentResponse, DeleteDepartmentResponse } from '../types/department';
import type { EdgeDevicesAPIResponse, EdgeDevice, CreateEdgeDeviceRequest, CreateEdgeDeviceResponse } from '../types/edgedevice';
import type { FaceImageAPIResponse, FaceImageFilters, EmployeesAggregateResponse, EmployeesAggregateFilters } from '../types/faceimage';
import type { CalendarsResponse, CreateCalendarRequest, CreateCalendarResponse, UpdateCalendarRequest, UpdateCalendarResponse } from '../types/calendar';
import type { FaceValidationFilters, FaceValidationResponse, ValidateFaceRequest, ValidateFaceResponse } from '../types/facevalidation';
import type { DashboardResponse, DashboardFilters } from '../types/dashboard';
import type { BreakByEmployeeFilters, BreakByEmployeeResponse } from '../types/faceimage';
import type { 
  VacationListResponse, 
  VacationListWithEmployeeResponse, 
  CreateVacationRequest, 
  CreateVacationResponse, 
  UpdateVacationRequest,
  UpdateVacationResponse,
  DeleteVacationResponse,
  VacationFilters, 
  CompanyVacationFilters 
} from '../types/vacation';
import type {
  HolidayListResponse,
  CreateHolidayRequest,
  CreateHolidayResponse,
  UpdateHolidayRequest,
  UpdateHolidayResponse,
  DeleteHolidayResponse
} from '../types/holiday';
import type {
  CreatePolicyRequest,
  CreatePolicyResponse,
  PoliciesAPIResponse,
  PolicyFilters
} from '../types/policy';
import type { AppearanceThresholdResponse, SaveAppearanceThresholdResponse } from '../types/appearanceThreshold';
import type {
  CreateCompanyGroupRequest,
  UpdateCompanyGroupRequest,
  CreateCompanyLocationRequest,
  UpdateCompanyLocationRequest,
  CompanyGroupsResponse,
  CompanyGroupResponse,
  CompanyLocationsResponse,
  CompanyLocationResponse,
  DeleteResponse,
  EffectiveSettingsResponse
} from '../types/companyGroup';
import type { 
  PPEFilters, 
  PPEResponse, 
  PPESummaryResponse,
  PPEDepartmentRequirement,
  PPEDepartmentRequirementsResponse,
  PPEViolationsResponse,
  PPEMonthlySummaryResponse,
  PPEEmployeeViolationHistoryResponse,
  } from '../types/ppe';
import type {
  ReportSchema,
  ReportPreviewRequest,
  ReportPreviewResponse,
  SavedReport,
  SaveReportRequest,
  ListSavedReportsResponse,
  ExportReportRequest,
  ExportJobStatus
} from '../types/customReport';
import type {
  EmployeeLoginRequest,
  EmployeeLoginResponse,
  EmployeeChangePasswordRequest,
  EmployeeMeResponse,
  AbsenceReasonSubmitResponse,
  AbsenceReasonsListResponse,
  AbsenceReasonDeleteResponse,
  EmployeeNotificationsResponse,
  ShiftNotificationPreference,
  ShiftNotificationPreferenceResponse,
  EmployeeDeviceTokenRequest,
  EmployeeImagesResponse as EmployeePortalImagesResponse,
  EmployeeImageUploadResponse
} from '../types/employeePortal';
import type {
  NotificationFilters,
  NotificationsListResponse,
  UnreadCountResponse,
  MarkReadResponse,
  MarkAllReadResponse,
  AcknowledgeResponse,
  ResolveResponse,
  DeviceTokenRequest
} from '../types/notification';
import type {
  SnapshotSettingsResponse,
  UpdateSnapshotSettingsRequest,
  SnapshotsListResponse,
  SnapshotFilters,
  SnapshotDeleteResponse,
  BulkDeleteSnapshotsRequest,
  AnalysisPromptsListResponse,
  CreateAnalysisPromptRequest,
  UpdateAnalysisPromptRequest,
  AnalysisPromptResponse,
  AnalysisPromptDeleteResponse,
  AnalyzeSnapshotRequest,
  AnalyzeSnapshotResponse,
  AnalysisHistoryResponse,
  StoreSnapshotResponse,
} from '../types/snapshot';
import type {
  DeskOccupancyStateResponse,
  DeskOccupancyHistoryResponse,
  DeskOccupancyHistoryFilters,
  DeskZonesResponse,
  UpdateDeskZonesRequest,
  DeskZonesMutationResponse,
} from '../types/deskOccupancy';
import type { DeskActivityStateResponse, DeskActivityHistoryResponse, DeskActivitySummaryResponse } from '../types/deskActivity';
import type {
  AnalyticsDepartmentRow,
  AnalyticsEmployeeRow,
  AnalyticsKpiKey,
  AnalyticsLateArrivalsResponse,
  AnalyticsLeaderboardResponse,
  AnalyticsPeriodType,
  AnalyticsSortDirection,
  AnalyticsSummaryResponse,
  AnalyticsTableResponse,
  AnalyticsTrendsResponse,
} from '../types/analytics';
import type {
  ShiftTemplate,
  CreateShiftTemplateRequest,
  UpdateShiftTemplateRequest,
  ShiftTemplateListResponse,
  ShiftAssignment,
  CreateShiftAssignmentRequest,
  BulkShiftAssignmentRequest,
  UpdateShiftAssignmentRequest,
  ShiftAssignmentListResponse,
  ShiftRotationGroup,
  CreateRotationGroupRequest,
  UpdateRotationGroupRequest,
  RotationGroupListResponse,
  GenerateRotationRequest,
  ShiftSwapRequest,
  RespondSwapRequest,
  ShiftSwapCandidate,
  ReviewSwapRequest,
  SwapRequestListResponse,
  ScheduleResponse,
  EmployeeScheduleResponse,
  StaffingResponse,
} from '../types/shift';
import type {
  PayrollSettingsResponse,
  UpdatePayrollSettingsRequest,
  UpdatePushSettingsRequest,
  CreatePayComponentRequest,
  UpdatePayComponentRequest,
  PayComponentListResponse,
  PayComponentResponse,
  UpdateEmployeePaySetupRequest,
  EmployeePaySetupResponse,
  PayrollExceptionFilters,
  ReviewExceptionRequest,
  RecomputeExceptionsRequest,
  PayrollExceptionListResponse,
  PayrollExceptionsSummaryResponse,
  CreatePeriodRequest,
  PayrollPeriodFilters,
  PayrollPeriodListResponse,
  PayrollPeriodResponse,
  PayrollTimesheet,
  PayrollTimesheetFilters,
  PayrollTimesheetListResponse,
  PayrollPayslipFilters,
  PayrollPayslipListResponse,
  PayrollPayslipResponse,
  PayrollAuditLogResponse,
  PayrollPushDeliveryListResponse,
  PayrollOverviewResponse,
  PayrollExportJobStatus,
  PayrollExportStartResponse,
  PayrollExportType,
  EmployeePortalPayslipsResponse,
  EmployeePortalPayslipResponse,
  EmployeePortalExceptionsResponse,
} from '../types/payroll';

// Use proxy in development, environment variable or fallback URL
const API_BASE_URL = import.meta.env.DEV 
  ? '/api/' 
  : import.meta.env.VITE_API_URL || 'https://api.attendnow.ai/api/';

// Only log in development
const isDev = import.meta.env.DEV;
const log = (message: string, ...args: any[]) => {
  if (isDev) console.log(message, ...args);
};

const logError = (message: string, ...args: any[]) => {
  if (args.some(isRequestCanceled)) return;
  if (isDev) console.error(message, ...args);
};

log('🌐 API Base URL:', API_BASE_URL);
log('🔧 Environment:', isDev ? 'Development' : 'Production');

// Create axios instance with optimized defaults
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 second timeout (increased for large data requests)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios instance for auth endpoints (no automatic retry)
const authApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate axios client for the employee portal. Do NOT reuse apiClient
const employeeApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

employeeApiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('employee_jwttoken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let isRefreshingEmployee = false;
let employeeFailedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processEmployeeQueue = (error: any, token: string | null = null) => {
  employeeFailedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  employeeFailedQueue = [];
};

const refreshEmployeeToken = async (): Promise<string> => {
  const refreshToken = localStorage.getItem('employee_refresh_token');
  if (!refreshToken) {
    throw new Error('No employee refresh token available');
  }

  try {
    log('🔄 Attempting to refresh employee token...');
    const response = await employeePortalAPI.refreshToken(refreshToken);
    if (response.success && response.token) {
      localStorage.setItem('employee_jwttoken', response.token);
      if (response.refresh_token) {
        localStorage.setItem('employee_refresh_token', response.refresh_token);
      }
      log('✅ Employee token refreshed successfully');
      return response.token;
    } else {
      throw new Error(response.message || 'Invalid employee refresh response');
    }
  } catch (error: any) {
    logError('❌ Employee refresh token failed:', error);
    
    // Clear employee auth data on refresh failure
    localStorage.removeItem('employee_jwttoken');
    localStorage.removeItem('employee_refresh_token');
    localStorage.removeItem('employee_user');
    localStorage.removeItem('employee_must_change_password');
    
    // Redirect to employee login
    window.location.href = '/employee/login';
    
    throw error;
  }
};

employeeApiClient.interceptors.response.use(
  (response) => {
    // Check if the response indicates token expiration even with 200 status
    if (response.data && response.data.success === 0 && 
        response.data.errors === "Token is Expired") {
      log('🔍 Detected employee token expiration in response body');
      
      const originalRequest = response.config;

      if (!isRefreshingEmployee) {
        isRefreshingEmployee = true;
        
        return refreshEmployeeToken()
          .then((newToken) => {
            isRefreshingEmployee = false;
            processEmployeeQueue(null, newToken);
            
            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return employeeApiClient.request(originalRequest);
          })
          .catch((error) => {
            isRefreshingEmployee = false;
            processEmployeeQueue(error, null);
            return Promise.reject(error);
          });
      }
      
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        employeeFailedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return employeeApiClient.request(originalRequest);
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
    
    return response;
  },
  async (error) => {
    if (isRequestCanceled(error)) return Promise.reject(error);
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshingEmployee) {
        return new Promise((resolve, reject) => {
          employeeFailedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return employeeApiClient.request(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshingEmployee = true;

      return refreshEmployeeToken()
        .then((newToken) => {
          isRefreshingEmployee = false;
          processEmployeeQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return employeeApiClient.request(originalRequest);
        })
        .catch((err) => {
          isRefreshingEmployee = false;
          processEmployeeQueue(err, null);
          return Promise.reject(err);
        });
    }
    return Promise.reject(error);
  }
);

// Cache for API responses to reduce redundant requests
const cache = new Map<string, { data: any; timestamp: number; ttl: number }>();

const getCachedData = (key: string): any | null => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < cached.ttl) {
    return cached.data;
  }
  cache.delete(key);
  return null;
};

const setCachedData = (key: string, data: any, ttlMinutes: number = 5): void => {
  cache.set(key, {
    data,
    timestamp: Date.now(),
    ttl: ttlMinutes * 60 * 1000,
  });
};

/** Omitted query parameters preserve the existing full-list behavior. */
export interface PaginationParams {
  page?: number;
  per_page?: number;
}

export interface EmployeeListParams extends PaginationParams {
  search?: string;
  sort?: 'name' | 'emp_id' | 'position' | 'hire_date' | 'end_of_hire_date';
  direction?: 'asc' | 'desc';
  images?: 'with' | 'without';
  department_id?: string | number;
  fields?: 'minimal';
  limit?: number;
  all_employees?: boolean | number;
}

export interface UserListParams extends PaginationParams {
  search?: string;
  limit?: number;
}

export interface TimelineParams extends PaginationParams {
  date?: string;
}

/** Callers should return before setting an error state when this returns true. */
export const isRequestCanceled = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (typeof error === 'object' && error !== null &&
    (('name' in error && error.name === 'AbortError') ||
      ('code' in error && error.code === 'ERR_CANCELED')));

const throwIfCanceled = (signal?: AbortSignal): void => {
  if (signal?.aborted) throw new axios.CanceledError('canceled');
};

const queryParams = (values: object): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(values).sort(([a], [b]) => a.localeCompare(b)).forEach(([key, value]) => {
    // Empty query strings may be converted to null before API validation.
    // An empty search means no filter, so omit it from both URL and cache key.
    if (key === 'search' && typeof value === 'string' && !value.trim()) return;
    if (value !== undefined && value !== null) params.append(key, String(value));
  });
  return params;
};

// Track pending reads so invalidation prevents an older response from restoring stale cache data.
const pendingCacheReads = new Map<string, Set<{ invalidated: boolean }>>();
const sharedListRequests = new Map<string, Promise<unknown>>();
const cachedListRequest = async <T>(
  url: string,
  cacheKey: string,
  ttl: number,
  config: AxiosRequestConfig = {},
  bypassCache = false,
): Promise<T> => {
  const signal = config.signal as AbortSignal | undefined;
  throwIfCanceled(signal);
  if (!bypassCache) {
    const cached = getCachedData(cacheKey);
    if (cached !== null) return cached;
  }
  if (!signal && !bypassCache) {
    const pending = sharedListRequests.get(cacheKey);
    if (pending) return pending as Promise<T>;
  }
  sharedListRequests.delete(cacheKey);
  const read = { invalidated: false };
  const reads = pendingCacheReads.get(cacheKey) ?? new Set();
  reads.forEach(previous => { previous.invalidated = true; });
  reads.add(read);
  pendingCacheReads.set(cacheKey, reads);
  const request = (async () => {
    try {
      const response = await apiClient.get<T>(url, config);
      throwIfCanceled(signal);
      if (!read.invalidated) setCachedData(cacheKey, response.data, ttl);
      return response.data;
    } finally {
      reads.delete(read);
      if (!reads.size) pendingCacheReads.delete(cacheKey);
    }
  })();
  if (!signal && !bypassCache) {
    sharedListRequests.set(cacheKey, request);
    try {
      return await request;
    } finally {
      if (sharedListRequests.get(cacheKey) === request) sharedListRequests.delete(cacheKey);
    }
  }
  return request;
};

const invalidateUserLists = (companyId: string | number, userId?: number): void => {
  clearCache(`usersByCompany_${companyId}_`);
  // Every company context can contain a cached copy of the global directory.
  clearCache('users_global_');
  clearCache(userId === undefined ? `user_assignment_${companyId}_` : `user_assignment_${companyId}_${userId}`);
};

// Token refresh logic
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const refreshToken = async (): Promise<string> => {
  const refreshTokenValue = localStorage.getItem('refresh_token');
  
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    log('🔄 Attempting to refresh token...');
    
    const response = await authApiClient.post('user/refresh-token', {
      refresh_token: refreshTokenValue
    });

    if (response.data?.access_token) {
      const newToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;
      
      // Update stored tokens
      localStorage.setItem('jwttoken', newToken);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      
      log('✅ Token refreshed successfully');
      return newToken;
    } else {
      throw new Error('Invalid refresh response');
    }
  } catch (error: any) {
    logError('❌ Token refresh failed:', error);
    
    // Clear all cached data on refresh failure to ensure no stale data
    clearCache();
    
    // Clear all auth data on refresh failure
    localStorage.removeItem('user');
    localStorage.removeItem('jwttoken');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('currentCompany');
    
    // Redirect to login
    window.location.href = '/login';
    
    throw error;
  }
};

// Add request interceptor for general API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('jwttoken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor for auth API calls (no token needed for login)
authApiClient.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for general API calls (handles both 401 and "Token is Expired" messages)
apiClient.interceptors.response.use(
  (response) => {
    // Check if the response indicates token expiration even with 200 status
    if (response.data && response.data.success === 0 && 
        response.data.errors === "Token is Expired") {
      log('🔍 Detected token expiration in response body');
      
      // Handle token refresh
      if (!isRefreshing) {
        isRefreshing = true;
        
        return refreshToken()
          .then((newToken) => {
            isRefreshing = false;
            processQueue(null, newToken);
            
            // Retry the original request with new token
            const originalConfig = response.config;
            originalConfig.headers.Authorization = `Bearer ${newToken}`;
            return apiClient.request(originalConfig);
          })
          .catch((error) => {
            isRefreshing = false;
            processQueue(error, null);
            return Promise.reject(error);
          });
      }
      
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((newToken) => {
          const originalConfig = response.config;
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(originalConfig);
        })
        .catch((error) => {
          return Promise.reject(error);
        });
    }
    
    return response;
  },
  (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return apiClient.request(originalRequest);
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      return refreshToken()
        .then((newToken) => {
          isRefreshing = false;
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient.request(originalRequest);
        })
        .catch((error) => {
          isRefreshing = false;
          processQueue(error, null);
          return Promise.reject(error);
        });
    }
    
    return Promise.reject(error);
  }
);

// Add response interceptor for auth API calls (no auto-redirect, let components handle errors)
authApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Enhanced error logging for debugging CORS and network issues
    if (error.code === 'ERR_NETWORK') {
      logError('🚨 Network Error: Unable to reach the server. This might be a CORS issue.');
      logError('🔍 Check if the backend server allows your domain:', window.location.origin);
    } else if (error.response) {
      logError('🚨 API Error Response:', error.response.status, error.response.data);
    } else if (error.request) {
      logError('🚨 No Response from Server:', error.request);
    }
    
    // Don't auto-redirect for auth calls - let the component handle the error
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials: LoginRequest): Promise<LoginResponse> => {
    return authApiClient.post('user/login', credentials).then(response => {
      return response.data;
    }).catch(error => {
      throw error;
    });
  },
  
  refreshToken: (): Promise<{ success: boolean; access_token: string; refresh_token: string }> => {
    const refreshTokenValue = localStorage.getItem('refresh_token');
    
    if (!refreshTokenValue) {
      throw new Error('No refresh token available');
    }
    
    return authApiClient.post('user/refresh-token', {
      refresh_token: refreshTokenValue
    }).then(response => {
      return response.data;
    }).catch(error => {
      throw error;
    });
  },

  createUser: (userData: CreateUserRequest): Promise<CreateUserResponse> => {
    log('🌐 API: Making POST request to /create-user/' + userData.company_id);
    const { company_id, ...payload } = userData;
    return apiClient.post(`create-user/${company_id}`, payload).then(response => {
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(company_id);
      log('🌐 API: POST /create-user/' + userData.company_id + ' - Status:', response.status);
      log('🌐 API: POST /create-user/' + userData.company_id + ' - Response:', response.data);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-user/' + userData.company_id + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getUsers: (companyId: string, params: UserListParams = {}, signal?: AbortSignal): Promise<UsersResponse> => {
    const qs = queryParams(params).toString();
    return cachedListRequest(`usersByCompany/${companyId}${qs ? `?${qs}` : ''}`,
      `usersByCompany_${companyId}_${qs}`, 5, { signal });
  },

  /**
   * Full user directory for super-admin flows (e.g. user–company assignment).
   * GET /users/{company_id} — path selects access/permission context and role display per user_company_assignments;
   * the list is global, not SQL-filtered to members of that company only.
   */
  getUsersGlobalList: (companyId: string, params: Pick<UserListParams, 'search' | 'limit'> = {}, signal?: AbortSignal): Promise<UsersResponse> => {
    const qs = queryParams(params).toString();
    return cachedListRequest(`users/${companyId}${qs ? `?${qs}` : ''}`,
      `users_global_${companyId}_${qs}`, 5, { signal });
  },

  updateUser: (companyId: string, userId: number, payload: Partial<CreateUserRequest>): Promise<{ success: boolean; message: string; data: User }> => {
    log(`🌐 API: Making PUT request to /api/companies/${companyId}/users/${userId}`);
    return apiClient.put(`companies/${companyId}/users/${userId}`, payload).then(response => {
      // Clear users cache for this company
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId, userId);
      return response.data;
    });
  },

  deleteUser: (companyId: string, userId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /api/companies/${companyId}/users/${userId}`);
    return apiClient.delete(`companies/${companyId}/users/${userId}`).then(response => {
      // Clear users cache for this company
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId, userId);
      return response.data;
    });
  },

  getRoles: (companyId: string): Promise<RolesResponse> => {
    // Check cache first
    const cacheKey = `roles_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached roles data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /roles?company_id=' + companyId);
    return apiClient.get(`roles?company_id=${companyId}`).then(response => {
      log('🌐 API: GET /roles - Status:', response.status);
      log('🌐 API: GET /roles - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /roles - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createUserCompanyAssignment: (assignmentData: CreateUserCompanyAssignmentRequest): Promise<CreateUserCompanyAssignmentResponse> => {
    log('🌐 API: Making POST request to /user-company-assignments');
    return apiClient.post('user-company-assignments', assignmentData).then(response => {
      log('🌐 API: POST /user-company-assignments - Status:', response.status);
      log('🌐 API: POST /user-company-assignments - Response:', response.data);
      
      // Clear users cache since assignments might affect user data
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(assignmentData.company_id, assignmentData.user_id);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /user-company-assignments - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getUserCompanyAssignment: (companyId: string, userId: number, signal?: AbortSignal): Promise<UserCompanyAssignmentResponse> => {
    const cacheKey = `user_assignment_${companyId}_${userId}`;
    return cachedListRequest(`user-company-assignments/${companyId}?user_id=${userId}`,
      cacheKey, 5, { signal });
  },

  getRolesSchema: (): Promise<RolesSchemaResponse> => {
    log('🌐 API: Making GET request to /roles/schema');
    return apiClient.get('roles/schema').then(response => {
      return response.data;
    });
  },

  getDepartmentAssignments: (companyId: string, userId: number): Promise<{ success: boolean; data: DepartmentAssignment[] }> => {
    log(`🌐 API: Making GET request to /companies/${companyId}/user-department-assignments/${userId}`);
    return apiClient.get(`companies/${companyId}/user-department-assignments/${userId}`).then(response => {
      return response.data;
    });
  },

  assignDepartments: (companyId: string, payload: { user_id: number; department_ids: number[] }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making POST request to /companies/${companyId}/user-department-assignments`);
    return apiClient.post(`companies/${companyId}/user-department-assignments`, payload).then(response => {
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId);
      return response.data;
    });
  },

  removeDepartmentAssignment: (companyId: string, assignmentId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /companies/${companyId}/user-department-assignments/${assignmentId}`);
    return apiClient.delete(`companies/${companyId}/user-department-assignments/${assignmentId}`).then(response => {
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId);
      return response.data;
    });
  },

  getEmployeeAssignments: (companyId: string, userId: number): Promise<{ success: boolean; data: EmployeeAssignment[] }> => {
    log(`🌐 API: Making GET request to /companies/${companyId}/user-employee-assignments/${userId}`);
    return apiClient.get(`companies/${companyId}/user-employee-assignments/${userId}`).then(response => {
      return response.data;
    });
  },

  assignEmployees: (companyId: string, payload: { user_id: number; employee_ids: number[] }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making POST request to /companies/${companyId}/user-employee-assignments`);
    return apiClient.post(`companies/${companyId}/user-employee-assignments`, payload).then(response => {
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId);
      return response.data;
    });
  },

  removeEmployeeAssignment: (companyId: string, assignmentId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /companies/${companyId}/user-employee-assignments/${assignmentId}`);
    return apiClient.delete(`companies/${companyId}/user-employee-assignments/${assignmentId}`).then(response => {
      if (response.data.success !== false && response.data.success !== 0) invalidateUserLists(companyId);
      return response.data;
    });
  },

  getRole: (companyId: string, roleId: number): Promise<{ success: boolean; data: Role }> => {
    log(`🌐 API: Making GET request to /companies/${companyId}/roles/${roleId}`);
    return apiClient.get(`companies/${companyId}/roles/${roleId}`).then(response => {
      return response.data;
    });
  },

  createRole: (payload: { company_id: string; role_name: string; role: Record<string, ResourcePermission> }): Promise<{ success: boolean; message: string }> => {
    log('🌐 API: Making POST request to /create-role');
    return apiClient.post('create-role', payload).then(response => {
      // Clear roles cache for this company
      clearCache(`roles_${payload.company_id}`);
      return response.data;
    });
  },

  updateRole: (companyId: string, roleId: number, payload: { role_name?: string; role?: Record<string, ResourcePermission> }): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making PUT request to /companies/${companyId}/roles/${roleId}`);
    return apiClient.put(`companies/${companyId}/roles/${roleId}`, payload).then(response => {
      // Clear roles cache for this company
      clearCache(`roles_${companyId}`);
      return response.data;
    });
  },

  deleteRole: (companyId: string, roleId: number): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making DELETE request to /companies/${companyId}/roles/${roleId}`);
    return apiClient.delete(`companies/${companyId}/roles/${roleId}`).then(response => {
      // Clear roles cache for this company
      clearCache(`roles_${companyId}`);
      return response.data;
    });
  },

  forgotPassword: (payload: ForgotPasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making POST request to /user/forgot-password');
    return authApiClient.post('user/forgot-password', payload).then(response => {
      return response.data;
    });
  },

  resetPassword: (payload: ResetPasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making POST request to /user/reset-password');
    return authApiClient.post('user/reset-password', payload).then(response => {
      return response.data;
    });
  },

  changePassword: (payload: ChangePasswordRequest): Promise<ForgotPasswordResponse> => {
    log('🌐 API: Making PUT request to /user/change-password');
    return apiClient.put('user/change-password', payload).then(response => {
      return response.data;
    });
  },
};

export const companyAPI = {
  getCompanies: (): Promise<{ success: boolean; data: Company[] }> => {
    // Check cache first
    const cacheKey = 'companies';
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached companies data');
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /companies');
    return apiClient.get('companies').then(response => {
      log('🌐 API: GET /companies - Status:', response.status);
      log('🌐 API: GET /companies - Response:', response.data);
      
      // Cache the response for 10 minutes
      setCachedData(cacheKey, response.data, 10);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /companies - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
  
  getCompany: (companyId: string): Promise<{ success: boolean; data: Company }> => {
    // Check cache first
    const cacheKey = `company_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached company data for ID:', companyId);
      return Promise.resolve(cachedData);
    }

    return apiClient.get(`companies/${companyId}`).then(response => {
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  createCompany: (companyData: CreateCompanyRequest): Promise<CreateCompanyResponse> => {
    log('🌐 API: Making POST request to /create-comp');
    return apiClient.post('create-comp', companyData).then(response => {
      log('🌐 API: POST /create-comp - Status:', response.status);
      log('🌐 API: POST /create-comp - Response:', response.data);
      
      // Clear companies cache since new company was created
      clearCache('companies');
      clearCache('locations_');
      clearCache('groups_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-comp - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateCompany: (id: number, companyData: UpdateCompanyRequest): Promise<{ success: boolean; message: string }> => {
    log(`🌐 API: Making PUT request to /update-company/${id}`);
    return apiClient.put(`update-company/${id}`, companyData).then(response => {
      log(`🌐 API: PUT /update-company/${id} - Status:`, response.status);
      
      clearCache('companies');
      clearCache('locations_');
      clearCache('groups_');
      clearCache('effective_settings_');
      clearCache(`company_${id}`);
      
      return response.data;
    }).catch(error => {
      logError(`🌐 API: PUT /update-company/${id} - Error:`, error);
      throw error;
    });
  },

  unassignLocation: (companyId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.patch(`company/${companyId}/location-unassign`).then(response => {
       clearCache('companies');
      clearCache('locations_');
      clearCache('groups_');
      clearCache('effective_settings_');
      clearCache(`company_${companyId}`);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: PATCH /company/${companyId}/location-unassign - Error:`, error);
      throw error;
    });
  }
};

export const companyGroupAPI = {
  listGroups: (): Promise<CompanyGroupsResponse> => {
    const cacheKey = `groups_`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached groups data');
      return Promise.resolve(cachedData);
    }

    return apiClient.get(`companies/groups`).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  getGroup: (groupId: number): Promise<CompanyGroupResponse> => {
    return apiClient.get(`companies/groups/${groupId}`).then(response => {
      return response.data;
    });
  },

  createGroup: (body: CreateCompanyGroupRequest): Promise<CompanyGroupResponse> => {
    return apiClient.post(`companies/groups`, body).then(response => {
      clearCache(`groups_`);
      clearCache(`locations_`);
      clearCache(`companies`);
      return response.data;
    });
  },

  updateGroup: (groupId: number, body: UpdateCompanyGroupRequest): Promise<CompanyGroupResponse> => {
    return apiClient.put(`companies/groups/${groupId}`, body).then(response => {
      clearCache(`groups_`);
      clearCache(`locations_`);
      clearCache(`companies`);
      clearCache(`effective_settings_`); // Clear all effective settings as updates can affect children
      return response.data;
    });
  },

  deleteGroup: (groupId: number): Promise<DeleteResponse> => {
    return apiClient.delete(`companies/groups/${groupId}`).then(response => {
      clearCache(`groups_`);
      clearCache(`locations_`);
      clearCache(`effective_settings_`);
      return response.data;
    });
  },
};

export const companyLocationAPI = {
  listLocations: (companyGroupId: number): Promise<CompanyLocationsResponse> => {
    const cacheKey = `locations_group_${companyGroupId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached locations data for group:', companyGroupId);
      return Promise.resolve(cachedData);
    }

    return apiClient.get(`groups/${companyGroupId}/locations`).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  getLocation: (companyGroupId: number, locationId: number): Promise<CompanyLocationResponse> => {
    return apiClient.get(`groups/${companyGroupId}/locations/${locationId}`).then(response => {
      return response.data;
    });
  },

  createLocation: (companyGroupId: number, body: CreateCompanyLocationRequest): Promise<CompanyLocationResponse> => {
    return apiClient.post(`groups/${companyGroupId}/locations`, body).then(response => {
      clearCache(`locations_group_${companyGroupId}`);
      return response.data;
    });
  },

  updateLocation: (companyGroupId: number, locationId: number, body: UpdateCompanyLocationRequest): Promise<CompanyLocationResponse> => {
    return apiClient.put(`groups/${companyGroupId}/locations/${locationId}`, body).then(response => {
      clearCache(`locations_group_${companyGroupId}`);
      clearCache(`companies`);
      clearCache(`effective_settings_location_${locationId}`);
      return response.data;
    });
  },

  deleteLocation: (companyGroupId: number, locationId: number): Promise<DeleteResponse> => {
    return apiClient.delete(`groups/${companyGroupId}/locations/${locationId}`).then(response => {
      clearCache(`locations_group_${companyGroupId}`);
      clearCache(`effective_settings_location_${locationId}`);
      return response.data;
    });
  },
};

export const effectiveSettingsAPI = {
  getCompanySettings: (companyId: string): Promise<EffectiveSettingsResponse> => {
    const cacheKey = `effective_settings_${companyId}_company`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    return apiClient.get(`companies/${companyId}/effective-settings`).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  getGroupSettings: (companyId: string, groupId: number): Promise<EffectiveSettingsResponse> => {
    const cacheKey = `effective_settings_${companyId}_group_${groupId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    return apiClient.get(`companies/${companyId}/groups/${groupId}/effective-settings`).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  getLocationSettings: (companyGroupId: number, locationId: number): Promise<EffectiveSettingsResponse> => {
    const cacheKey = `effective_settings_group_${companyGroupId}_location_${locationId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    return apiClient.get(`groups/${companyGroupId}/locations/${locationId}/effective-settings`).then(response => {
    setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },
};

export const appearanceThresholdsAPI = {
  getThreshold: (companyId: string): Promise<AppearanceThresholdResponse> => {
    const cacheKey = `appearance_threshold_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached appearance threshold for company:', companyId);
      return Promise.resolve(cachedData);
    }
    return apiClient.get(`appearance-thresholds/${companyId}`).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },

  saveThreshold: (body: { company_id: string; threshold_minutes: number }): Promise<SaveAppearanceThresholdResponse> => {
    return apiClient.post('appearance-thresholds', body).then(response => {
      clearCache(`appearance_threshold_${body.company_id}`);
      return response.data;
    });
  },
};

export const cameraAPI = {
  getCameras: (companyId: string): Promise<CamerasResponse> => {
    // Check cache first
    const cacheKey = `cameras_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached cameras data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /cameras/' + companyId);
    return apiClient.get(`cameras/${companyId}`).then(response => {
      log('🌐 API: GET /cameras/' + companyId + ' - Status:', response.status);
      log('🌐 API: GET /cameras/' + companyId + ' - Response:', response.data);
      
      // Cache for 3 minutes (cameras might change more frequently)
      setCachedData(cacheKey, response.data, 3);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /cameras/' + companyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
  
  getCameraDetails: (id: number): Promise<{ success: boolean; camera: Camera }> => {
    // Check cache first
    const cacheKey = `camera_${id}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached camera details for ID:', id);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /camera/' + id + ' (single camera)');
    return apiClient.get(`camera/${id}`).then(response => {
      log('🌐 API: GET /camera/' + id + ' - Status:', response.status);
      log('🌐 API: GET /camera/' + id + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /camera/' + id + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createCamera: (cameraData: CreateCameraRequest): Promise<CreateCameraResponse> => {
    log('🌐 API: Making POST request to /create-cam');
    return apiClient.post('create-cam', cameraData).then(response => {
      log('🌐 API: POST /create-cam - Status:', response.status);
      log('🌐 API: POST /create-cam - Response:', response.data);
      
      // Clear cache for cameras of this company
      clearCache(`cameras_${cameraData.company_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-cam - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateCamera: (cameraId: number, cameraData: UpdateCameraRequest, companyId: string): Promise<UpdateCameraResponse> => {
    log('🌐 API: Making PUT request to /camera/' + cameraId + '/update');
    log('🌐 API: Request payload:', cameraData);
    return apiClient.put(`camera/${cameraId}/update`, cameraData).then(response => {
      log('🌐 API: PUT /camera/' + cameraId + '/update - Status:', response.status);
      log('🌐 API: PUT /camera/' + cameraId + '/update - Response:', response.data);
      
      // Clear cache for cameras of this company and camera details
      clearCache(`cameras_${companyId}`);
      clearCache(`camera_${cameraId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /camera/' + cameraId + '/update - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const employeeAPI = {
  getEmployeeById: (employeeId: string, signal?: AbortSignal): Promise<{
    success: boolean;
    employee: Pick<Employee, 'id' | 'name' | 'emp_id' | 'company_id'> & {
      department?: string | number | null;
      department_id?: number;
    };
  }> => apiClient.get(`employee/${encodeURIComponent(employeeId)}`, { signal }).then(response => response.data),

  // Supports legacy (company, includeInactive, params?, signal?) and (company, params, signal?).
  getEmployees: (
    companyId: string,
    includeInactiveOrParams: boolean | EmployeeListParams = false,
    paramsOrSignal: EmployeeListParams | AbortSignal = {},
    signal?: AbortSignal,
  ): Promise<EmployeesResponse> => {
    const isSignal = 'aborted' in paramsOrSignal;
    const params = typeof includeInactiveOrParams === 'boolean'
      ? { ...(!isSignal ? paramsOrSignal : {}), ...(includeInactiveOrParams ? { all_employees: 1 } : {}) }
      : includeInactiveOrParams;
    const requestSignal = isSignal ? paramsOrSignal : signal;
    const qs = queryParams(params).toString();
    return cachedListRequest(`employees/${companyId}${qs ? `?${qs}` : ''}`,
      `employees_${companyId}_${qs}`, 5, { signal: requestSignal });
  },

  bulkImport: (companyId: string, body: BulkImportRequest, mode: 'strict' | 'partial' = 'strict'): Promise<BulkImportResponse> => {
    log('🌐 API: Making POST request to /employees/' + companyId + '/import?mode=' + mode);
    return apiClient.post(`employees/${companyId}/import?mode=${mode}`, body).then(response => {
      log('🌐 API: POST /employees/' + companyId + '/import - Status:', response.status);
      log('🌐 API: POST /employees/' + companyId + '/import - Response:', response.data);
      
      // Clear cache for employees of this company
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /employees/' + companyId + '/import - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createEmployee: (employeeData: CreateEmployeeRequest | FormData): Promise<CreateEmployeeResponse> => {
    log('🌐 API: Making POST request to /create-emp');
    
    const isFormData = employeeData instanceof FormData;
    const config = isFormData ? { 
      headers: { 
        'Content-Type': undefined 
      } 
    } : {};

    return apiClient.post('create-emp', employeeData, config).then(response => {
      log('🌐 API: POST /create-emp - Status:', response.status);
      log('🌐 API: POST /create-emp - Response:', response.data);
      
      // Clear cache for employees of this company
      let companyId = '';
      if (isFormData) {
        companyId = (employeeData as FormData).get('company_id') as string;
      } else {
        companyId = (employeeData as CreateEmployeeRequest).company_id;
      }
      
      if (companyId) {
        if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      }
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-emp - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateEmployee: (employeeId: number, employeeData: UpdateEmployeeRequest, companyId: string): Promise<UpdateEmployeeResponse> => {
    log('🌐 API: Making PUT request to /employee/' + employeeId + '/update');
    log('🌐 API: Request payload:', employeeData);
    return apiClient.put(`employee/${employeeId}/update`, employeeData).then(response => {
      log('🌐 API: PUT /employee/' + employeeId + '/update - Status:', response.status);
      log('🌐 API: PUT /employee/' + employeeId + '/update - Response:', response.data);
      
      // Clear cache for employees of this company
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /employee/' + employeeId + '/update - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  grantPortalAccess: (employeeId: number, data: { username: string; password: string }, companyId?: string) => {
    log('🌐 API: Making POST request to /employees/' + employeeId + '/portal-access');
    return apiClient.post(`employees/${employeeId}/portal-access`, data).then(r => {
      if (companyId) clearCache(`employees_${companyId}_`);
      return r.data;
    });
  },
  resetPortalPassword: (employeeId: number, data: { password: string }, companyId?: string) => {
    log('🌐 API: Making PUT request to /employees/' + employeeId + '/portal-access');
    return apiClient.put(`employees/${employeeId}/portal-access`, data).then(r => {
      if (companyId) clearCache(`employees_${companyId}_`);
      return r.data;
    });
  },
  revokePortalAccess: (employeeId: number, companyId?: string) => {
    log('🌐 API: Making DELETE request to /employees/' + employeeId + '/portal-access');
    return apiClient.delete(`employees/${employeeId}/portal-access`).then(r => {
      if (companyId) clearCache(`employees_${companyId}_`);
      return r.data;
    });
  },
};

export const employeePortalAPI = {
  login: (credentials: EmployeeLoginRequest): Promise<EmployeeLoginResponse> => {
    log('🌐 API: Making POST request to /employee/login');
    return authApiClient.post('employee/login', credentials).then(r => r.data);
  },
  refreshToken: (refresh_token: string) => {
    log('🌐 API: Making POST request to /employee/refresh-token');
    return authApiClient.post('employee/refresh-token', { refresh_token }).then(r => r.data);
  },
  changePassword: (data: EmployeeChangePasswordRequest) => {
    log('🌐 API: Making POST request to /employee/change-password');
    return employeeApiClient.post('employee/change-password', data).then(r => r.data);
  },
  getMe: (params?: { date_from?: string; date_to?: string }): Promise<EmployeeMeResponse> => {
    const url = 'employee/me';
    log('🌐 API: Making GET request to /' + url, params);
    return employeeApiClient.get(url, { params }).then(r => r.data);
  },
  submitAbsenceReason: (data: FormData): Promise<AbsenceReasonSubmitResponse> => {
    return employeeApiClient.post('employee/absence-reason', data, {
      headers: { 'Content-Type': undefined },
    }).then(r => r.data);
  },
  getAbsenceReasons: (params?: { date_from?: string; date_to?: string }): Promise<AbsenceReasonsListResponse> => {
    return employeeApiClient.get('employee/absence-reasons', { params }).then(r => r.data);
  },
  deleteAbsenceReason: (id: number): Promise<AbsenceReasonDeleteResponse> => {
    return employeeApiClient.delete(`employee/absence-reason/${id}`).then(r => r.data);
  },
  getNotifications: (params?: { page?: number; per_page?: number; unread_only?: boolean }): Promise<EmployeeNotificationsResponse> => {
    return employeeApiClient.get('employee/notifications', { params }).then(r => r.data);
  },
  markNotificationAsRead: (id: number): Promise<{ success: boolean; message?: string }> => {
    return employeeApiClient.put(`employee/notifications/${id}/mark-read`, {}).then(r => r.data);
  },
  getMySchedule: (params?: { date_from?: string; date_to?: string }): Promise<EmployeeScheduleResponse> => {
    log('🌐 API: Making GET request to /employee/schedule', params);
    return employeeApiClient.get('employee/schedule', { params }).then(r => r.data);
  },
  getMySwaps: (companyId: string, params?: { status?: string; page?: number; per_page?: number }): Promise<SwapRequestListResponse> => {
    log('🌐 API: Making GET request to /companies/${companyId}/shifts/swaps/mine', params);
    return employeeApiClient.get(`companies/${companyId}/shifts/swaps/mine`, { params }).then(r => r.data);
  },
  createSwapRequest: (companyId: string, data: { target_employee_id: number; swap_date: string; requester_reason?: string }): Promise<{ success: boolean; message?: string; data?: ShiftSwapRequest }> => {
    log('🌐 API: Making POST request to /companies/${companyId}/shifts/swaps', data);
    return employeeApiClient.post(`companies/${companyId}/shifts/swaps`, data).then(r => r.data);
  },
  getSwapCandidates: (swapDate: string): Promise<{ success: boolean; data: ShiftSwapCandidate[] }> => {
    log('🌐 API: Making GET request to /employee/swap-candidates', { swap_date: swapDate });
    return employeeApiClient.get('employee/swap-candidates', { params: { swap_date: swapDate } }).then(r => r.data);
  },
  respondToSwap: (companyId: string, swapId: number, data: RespondSwapRequest): Promise<{ success: boolean; message?: string; data?: ShiftSwapRequest }> => {
    log('🌐 API: Making PUT request to /companies/${companyId}/shifts/swaps/${swapId}/respond', data);
    return employeeApiClient.put(`companies/${companyId}/shifts/swaps/${swapId}/respond`, data).then(r => r.data);
  },
  cancelSwap: (companyId: string, swapId: number): Promise<{ success: boolean; message?: string }> => {
    log('🌐 API: Making PUT request to /companies/${companyId}/shifts/swaps/${swapId}/cancel');
    return employeeApiClient.put(`companies/${companyId}/shifts/swaps/${swapId}/cancel`).then(r => r.data);
  },
  getShiftNotificationPreferences: (companyId: string): Promise<ShiftNotificationPreferenceResponse> => {
    log('🌐 API: Making GET request to /companies/${companyId}/shifts/notification-preferences/employee');
    return employeeApiClient.get(`companies/${companyId}/shifts/notification-preferences/employee`).then(r => r.data);
  },
  updateShiftNotificationPreferences: (companyId: string, channels: ShiftNotificationPreference['channels']): Promise<ShiftNotificationPreferenceResponse> => {
    log('🌐 API: Making PUT request to /companies/${companyId}/shifts/notification-preferences/employee', { channels });
    return employeeApiClient.put(`companies/${companyId}/shifts/notification-preferences/employee`, { channels }).then(r => r.data);
  },
  registerDeviceToken: (data: EmployeeDeviceTokenRequest): Promise<{ success: boolean; message?: string }> => {
    log('🌐 API: Making POST request to /employee/device-tokens', { platform: data.platform });
    return employeeApiClient.post('employee/device-tokens', data).then(r => r.data);
  },
  unregisterDeviceToken: (data: { token: string }): Promise<{ success: boolean; message?: string }> => {
    log('🌐 API: Making DELETE request to /employee/device-tokens');
    return employeeApiClient.delete('employee/device-tokens', { data }).then(r => r.data);
  },
  getImages: (signal?: AbortSignal): Promise<EmployeePortalImagesResponse> =>
    employeeApiClient.get('employee/images', { signal }).then(r => r.data),
  uploadImage: (
    data: FormData,
    onUploadProgress?: (progress: number) => void,
  ): Promise<EmployeeImageUploadResponse> =>
    employeeApiClient.post('employee/images', data, {
      headers: { 'Content-Type': undefined },
      onUploadProgress: event => {
        if (event.total && onUploadProgress) {
          onUploadProgress(Math.round((event.loaded * 100) / event.total));
        }
      },
    }).then(r => r.data),
};

export const employeeImagesAPI = {
  listEmployeeImages: (companyId: string, empId: string | number): Promise<EmployeeImagesResponse> => {
    log(`🌐 API: Making GET request to /employeesimages/${companyId}/${empId}`);
    return apiClient.get(`employeesimages/${companyId}/${empId}`).then(response => {
      log(`🌐 API: GET /employeesimages/${companyId}/${empId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: GET /employeesimages/${companyId}/${empId} - Error:`, error);
      throw error;
    });
  },

  uploadEmployeeImage: (companyId: string, empId: string | number, formData: FormData): Promise<EmployeeImageResponse> => {
    log(`🌐 API: Making POST request to /employeesimages/upload/${companyId}/${empId}`);
    return apiClient.post(`employeesimages/upload/${companyId}/${empId}`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(response => {
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      log(`🌐 API: POST /employeesimages/upload/${companyId}/${empId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: POST /employeesimages/upload/${companyId}/${empId} - Error:`, error);
      throw error;
    });
  },

  updateEmployeeImage: (companyId: string, empId: string | number, imageId: number, formData: FormData): Promise<EmployeeImageResponse> => {
    log(`🌐 API: Making POST request with _method=PUT to /employeesimages/${companyId}/${empId}/${imageId}`);
    
    // Ensure _method is set to PUT if not already present in FormData
    if (!formData.has('_method')) {
      formData.append('_method', 'PUT');
    }

    return apiClient.post(`employeesimages/${companyId}/${empId}/${imageId}`, formData, {
      headers: { 'Content-Type': undefined }
    }).then(response => {
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      log(`🌐 API: POST (PUT) /employeesimages/${companyId}/${empId}/${imageId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: POST (PUT) /employeesimages/${companyId}/${empId}/${imageId} - Error:`, error);
      throw error;
    });
  },

  deleteEmployeeImage: (companyId: string, empId: number, imageId: number): Promise<EmployeeImageDeleteResponse> => {
    log(`🌐 API: Making DELETE request to /employeesimages/${companyId}/${empId}/${imageId}`);
    return apiClient.delete(`employeesimages/${companyId}/${empId}/${imageId}`).then(response => {
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employees_${companyId}_`);
      log(`🌐 API: DELETE /employeesimages/${companyId}/${empId}/${imageId} - Status:`, response.status);
      return response.data;
    }).catch(error => {
      logError(`🌐 API: DELETE /employeesimages/${companyId}/${empId}/${imageId} - Error:`, error);
      throw error;
    });
  },
};

export const departmentAPI = {
  getDepartments: async (companyId: string, signal?: AbortSignal): Promise<DepartmentsResponse> => {
    throwIfCanceled(signal);
    // Check cache first
    const cacheKey = `departments_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached departments data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /departments/' + companyId);
    return cachedListRequest(`departments/${companyId}`, cacheKey, 5, { signal });
  },

  createDepartment: (departmentData: CreateDepartmentRequest): Promise<CreateDepartmentResponse> => {
    log('🌐 API: Making POST request to /create-dep');
    return apiClient.post('create-dep', departmentData).then(response => {
      log('🌐 API: POST /create-dep - Status:', response.status);
      log('🌐 API: POST /create-dep - Response:', response.data);
      
      // Clear cache for departments of this company
      clearCache(`departments_${departmentData.company_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-dep - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateDepartment: (departmentId: number, departmentData: UpdateDepartmentRequest, companyId: string): Promise<UpdateDepartmentResponse> => {
    log('🌐 API: Making PUT request to /department/' + departmentId + '/update');
    log('🌐 API: Request payload:', departmentData);
    return apiClient.put(`department/${departmentId}/update`, departmentData).then(response => {
      log('🌐 API: PUT /department/' + departmentId + '/update - Status:', response.status);
      log('🌐 API: PUT /department/' + departmentId + '/update - Response:', response.data);
      
      // Clear cache for departments of this company
      clearCache(`departments_${companyId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /department/' + departmentId + '/update - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  deleteDepartment: (departmentId: number, companyId: string): Promise<DeleteDepartmentResponse> => {
    log('🌐 API: Making DELETE request to /department/' + departmentId + '/delete');
    return apiClient.delete(`department/${departmentId}`).then(response => {
      log('🌐 API: DELETE /department/' + departmentId + '- Status:', response.status);
      log('🌐 API: DELETE /department/' + departmentId + '- Response:', response.data);
      
      // Clear cache for departments of this company
      clearCache(`departments_${companyId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /department/' + departmentId + '/delete - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const edgeDeviceAPI = {
  getEdgeDevices: (companyId: string): Promise<EdgeDevicesAPIResponse> => {
    // Check cache first
    const cacheKey = `edgedevices_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached edge devices data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /edgedevices/' + companyId);
    return apiClient.get(`edgedevices/${companyId}`).then(response => {
      log('🌐 API: GET /edgedevices/' + companyId + ' - Status:', response.status);
      log('🌐 API: GET /edgedevices/' + companyId + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      // Handle the specific case where backend returns 404 for "No edge devices found"
      if (error.response?.status === 404 && 
          error.response?.data?.message === "No edge devices found") {
        log('📝 No edge devices found for company:', companyId, '- returning empty array');
        
        // Return empty array as if it's a successful response
        const emptyResponse: EdgeDevice[] = [];
        
        // Cache the empty response to avoid repeated 404 calls
        setCachedData(cacheKey, emptyResponse, 5);
        
        return emptyResponse;
      }
      
      // For all other errors, log and throw as before
      logError('🌐 API: GET /edgedevices/' + companyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createEdgeDevice: (deviceData: CreateEdgeDeviceRequest): Promise<CreateEdgeDeviceResponse> => {
    log('🌐 API: Making POST request to /create-dev');
    return apiClient.post('create-dev', deviceData).then(response => {
      log('🌐 API: POST /create-dev - Status:', response.status);
      log('🌐 API: POST /create-dev - Response:', response.data);
      
      // Clear cache for edge devices of this company
      clearCache(`edgedevices_${deviceData.company_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-dev - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const calendarAPI = {
  getCalendars: (companyId: string): Promise<CalendarsResponse> => {
    // Check cache first
    const cacheKey = `calendars_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached calendars data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /calendars/' + companyId);
    return apiClient.get(`calendars/${companyId}`).then(response => {
      log('🌐 API: GET /calendars/' + companyId + ' - Status:', response.status);
      log('🌐 API: GET /calendars/' + companyId + ' - Response:', response.data);
      
      // Cache for 5 minutes
      setCachedData(cacheKey, response.data, 5);
      
      return response.data;
    }).catch(error => {
      // Handle the specific case where backend returns 404 for "No calendars found"
      if (error.response?.status === 404 && 
          error.response?.data?.message === "No calendars found") {
        log('📝 No calendars found for company:', companyId, '- returning empty array');
        
        // Return empty response as if it's a successful response
        const emptyResponse: CalendarsResponse = { calendars: [] };
        
        // Cache the empty response to avoid repeated 404 calls
        setCachedData(cacheKey, emptyResponse, 5);
        
        return emptyResponse;
      }
      
      // For all other errors, log and throw as before
      logError('🌐 API: GET /calendars/' + companyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createCalendar: (calendarData: CreateCalendarRequest): Promise<CreateCalendarResponse> => {
    log('🌐 API: Making POST request to /create-calendar');
    return apiClient.post('create-calendar', calendarData).then(response => {
      log('🌐 API: POST /create-calendar - Status:', response.status);
      log('🌐 API: POST /create-calendar - Response:', response.data);
      
      // Clear cache for calendars of this company
      clearCache(`calendars_${calendarData.company_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /create-calendar - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateCalendar: (calendarId: number, calendarData: UpdateCalendarRequest, companyId: string): Promise<UpdateCalendarResponse> => {
    log('🌐 API: Making PUT request to /update-calendar/' + calendarId);
    log('🌐 API: Request payload:', calendarData);
    return apiClient.put(`update-calendar/${calendarId}`, calendarData).then(response => {
      log('🌐 API: PUT /update-calendar/' + calendarId + ' - Status:', response.status);
      log('🌐 API: PUT /update-calendar/' + calendarId + ' - Response:', response.data);
      
      // Clear cache for calendars of this company
      clearCache(`calendars_${companyId}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /update-calendar/' + calendarId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const faceImageAPI = {
  getFaceImages: async (companyId: string, filters: FaceImageFilters = {}, bypassCache: boolean = false, signal?: AbortSignal): Promise<FaceImageAPIResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `faceimages_${companyId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached face images data for company:', companyId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for face images - fetching fresh data');
    }

    const url = `faceimage/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    return cachedListRequest(url, cacheKey, 2, { signal }, bypassCache);
  },

  getFaceImagesCSV: (companyId: string, filters: FaceImageFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `faceimageCSV/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    return apiClient.get(url, {
      responseType: 'blob',
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getEmployeesAggregate: (companyId: string, filters: EmployeesAggregateFilters = {}, bypassCache: boolean = false, signal?: AbortSignal): Promise<EmployeesAggregateResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the OLD endpoint that supports date ranges
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    if (filters.page !== undefined) params.append('page', String(filters.page));
    if (filters.per_page !== undefined) params.append('per_page', String(filters.per_page));
    const queryString = params.toString();
    const cacheKey = `employeesaggregate_${companyId}_${queryString}`;

    const url = `employeesAggregate/${companyId}${queryString ? `?${queryString}` : ''}`;
    return cachedListRequest(url, cacheKey, 3, { timeout: 30000, signal }, bypassCache);
  },

  getEmployeesAggBreak: (companyId: string, filters: EmployeesAggregateFilters = {}, bypassCache: boolean = false, signal?: AbortSignal): Promise<EmployeesAggregateResponse> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the NEW endpoint that uses attendDate
    if (filters.attendDate) params.append('attendDate', filters.attendDate);
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    if (filters.page !== undefined) params.append('page', String(filters.page));
    if (filters.per_page !== undefined) params.append('per_page', String(filters.per_page));
    const queryString = params.toString();
    const cacheKey = `employeesaggbreak_${companyId}_${queryString}`;

    const url = `EmployeesAggBreak/${companyId}${queryString ? `?${queryString}` : ''}`;
    return cachedListRequest(url, cacheKey, 3, { timeout: 30000, signal }, bypassCache);
  },

  getEmployeesAggregateCSV: (companyId: string, filters: EmployeesAggregateFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the OLD endpoint that supports date ranges
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `employeesAggregateCSV/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    // Use a longer timeout for aggregate CSV endpoint as it needs to process more data
    return apiClient.get(url, { 
      responseType: 'blob',
      timeout: 120000,
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getEmployeesAggBreakCSV: (companyId: string, filters: EmployeesAggregateFilters = {}, timezone: string = 'UTC'): Promise<Blob> => {
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - this is for the NEW endpoint that uses attendDate
    if (filters.attendDate) params.append('attendDate', filters.attendDate);
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    // Add timezone parameter
    params.append('timezone', timezone);
    
    const queryString = params.toString();
    const url = `EmployeesAggBreakCSV/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url + ' for CSV export with timezone:', timezone);
    
    // Use a longer timeout for aggregate CSV endpoint as it needs to process more data
    return apiClient.get(url, { 
      responseType: 'blob',
      timeout: 120000,
      headers: {
        'Accept': 'text/csv',
      }
    }).then(response => {
      log('🌐 API: GET /' + url + ' - CSV export successful');
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - CSV export error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // New endpoint: Get per-day break sessions and recent detections for a single employee (Dubai timezone)
  getBreakByEmployee: async (companyId: string, filters: BreakByEmployeeFilters, bypassCache: boolean = false, signal?: AbortSignal): Promise<BreakByEmployeeResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    
    // Required parameters
    params.append('from_date', filters.from_date);
    params.append('to_date', filters.to_date);
    params.append('employee', filters.employee); // This should be employee_id
    
    // Optional parameters
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    
    const queryString = params.toString();
    const cacheKey = `breakbyemployee_${companyId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached break by employee data for company:', companyId, 'employee:', filters.employee);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for break by employee - fetching fresh data');
    }

    // Use the correct endpoint path
    const url = `EmployeesAggBreakByEmp/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    // Use a longer timeout for this endpoint as it needs to process daily data
    return cachedListRequest(url, cacheKey, 5, { signal, timeout: 30000 }, bypassCache);
  },

  // Image proxy function to serve images through the API to avoid CORS issues
  getImageUrl: (companyId: string, cameraNumber: string, imageFile: string): string => {
    // In development, use the uploads proxy to avoid CORS issues
    if (import.meta.env.DEV) {
      return `/uploads/camera/${companyId}/${cameraNumber}/${imageFile}`;
    }
    
    // In production, use the API domain for images
    return `https://api.attendnow.ai/uploads/camera/${companyId}/${cameraNumber}/${imageFile}`;
  }
};

export const faceValidationAPI = {
  getFacesToValidate: async (companyId: string, filters: FaceValidationFilters = {}, bypassCache: boolean = false, signal?: AbortSignal): Promise<FaceValidationResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    
    // Add filters as query parameters - updated to match new API
    if (filters.validated !== undefined) params.append('validated', filters.validated ? 'true' : 'false');
    if (filters.employee_name) params.append('employee_name', filters.employee_name);
    if (filters.department_name) params.append('department_name', filters.department_name);
    if (filters.camera_number) params.append('camera_number', filters.camera_number);
    if (filters.camera_name) params.append('camera_name', filters.camera_name);
    if (filters.image_file) params.append('image_file', filters.image_file);
    if (filters.model_setup) params.append('model_setup', filters.model_setup);
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `validationresult_${companyId}_${queryString}`;
    
    // Check cache first (skip if bypassCache is true)
    if (!bypassCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        log('💾 Using cached validation result data for company:', companyId);
        return Promise.resolve(cachedData);
      }
    } else {
      log('🚀 Bypassing cache for validation result - fetching fresh data');
    }

    const url = `validation-result/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    
    return cachedListRequest(url, cacheKey, 2, { signal }, bypassCache);
  },

  validateFace: (validationData: ValidateFaceRequest): Promise<ValidateFaceResponse> => {
    log('🌐 API: Making POST request to /validate_face');
    log('🌐 API: Request payload:', validationData);
    return apiClient.post('validate_face', validationData).then(response => {
      log('🌐 API: POST /validate_face - Status:', response.status);
      log('🌐 API: POST /validate_face - Response:', response.data);
      
      // Clear cache for validation result since validation might affect the list
      if (response.data.success !== false && response.data.success !== 0) clearCache('validationresult_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /validate_face - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

export const ppeAPI = {
  getPPEDetections: async (companyId: string, filters: PPEFilters = {}, signal?: AbortSignal): Promise<PPEResponse> => {
    throwIfCanceled(signal);
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.employee_id) params.append('employee_id', filters.employee_id);
    if (filters.department_id) params.append('department_id', filters.department_id);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());

    const queryString = params.toString();
    const cacheKey = `ppe_detections_${companyId}_${queryString}`;
    const cachedData = getCachedData(cacheKey);
    const url = `companies/${companyId}/ppe${queryString ? `?${queryString}` : ''}`;

    if (cachedData) {
      log('💾 Using cached PPE detections:', cacheKey);
      log('🌐 API: GET /' + url + ' — cached body:', cachedData);
      return Promise.resolve(cachedData);
    }

    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url, { signal }).then(response => {
      const data = response.data;
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response body:', data);
      log('🌐 API: GET /' + url + ' - Shape:', {
        success: data?.success,
        ppe_types: data?.ppe_types,
        meta: data?.meta,
        dataLength: data?.data?.length,
        firstRow: data?.data?.[0],
      });
      setCachedData(cacheKey, data, 2);
      return data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  getPPESummary: async (companyId: string, filters: { date_from?: string; date_to?: string; department_id?: string } = {}, signal?: AbortSignal): Promise<PPESummaryResponse> => {
    throwIfCanceled(signal);
    const params = new URLSearchParams();
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.department_id) params.append('department_id', filters.department_id);

    const queryString = params.toString();
    const cacheKey = `ppe_summary_${companyId}_${queryString}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    const url = `companies/${companyId}/ppe/summary${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    return apiClient.get(url, { signal }).then(response => {
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    });
  },
};

export const dashboardAPI = {
  getDashboardData: async (companyId: string, filters: DashboardFilters = {}, signal?: AbortSignal): Promise<DashboardResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);

    const queryString = params.toString();
    const cacheKey = `dashboard_${companyId}_${queryString}`;

    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached dashboard data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `dash/${companyId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    // Dashboard can be slow with large datasets - increase timeout to 60 seconds
    return cachedListRequest(url, cacheKey, 5, { signal, timeout: 60000 });
  },
};

const cleanAnalyticsParams = (params: object) => {
  const cleaned: Record<string, unknown> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const analyticsPath = (companyId: string, endpoint: string) =>
  `companies/${companyId}/analytics/${endpoint}`;

export const analyticsAPI = {
  getSummary: (
    companyId: string,
    params: {
      period_type: AnalyticsPeriodType;
      period_start: string;
      compare_previous: boolean;
      department_id?: string;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsSummaryResponse> =>
    apiClient.get(analyticsPath(companyId, 'summary'), {
      signal,
      params: cleanAnalyticsParams({
        ...params,
        compare_previous: params.compare_previous ? 1 : 0,
      }),
    }).then(response => response.data),

  getTrends: (
    companyId: string,
    params: {
      kpi: AnalyticsKpiKey;
      granularity: AnalyticsPeriodType;
      from: string;
      to: string;
      department_id?: string;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsTrendsResponse> =>
    apiClient.get(analyticsPath(companyId, 'trends'), { params: cleanAnalyticsParams(params), signal }).then(response => response.data),

  getDepartments: (
    companyId: string,
    params: {
      period_type: AnalyticsPeriodType;
      period_start: string;
      sort_by: AnalyticsKpiKey;
      sort_direction: AnalyticsSortDirection;
      per_page: number;
      page: number;
      department_id?: string;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsTableResponse<AnalyticsDepartmentRow>> =>
    apiClient.get(analyticsPath(companyId, 'departments'), { params: cleanAnalyticsParams(params), signal }).then(response => response.data),

  getEmployees: (
    companyId: string,
    params: {
      period_type: AnalyticsPeriodType;
      period_start: string;
      sort_by: AnalyticsKpiKey;
      sort_direction: AnalyticsSortDirection;
      per_page: number;
      page: number;
      department_id?: string;
      min_punctuality?: string;
      max_absence?: string;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsTableResponse<AnalyticsEmployeeRow>> =>
    apiClient.get(analyticsPath(companyId, 'employees'), { params: cleanAnalyticsParams(params), signal }).then(response => response.data),

  getLeaderboard: (
    companyId: string,
    params: {
      kpi: AnalyticsKpiKey;
      period_type: AnalyticsPeriodType;
      period_start: string;
      direction: 'top' | 'bottom' | 'both';
      limit: number;
      department_id?: string;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsLeaderboardResponse> =>
    apiClient.get(analyticsPath(companyId, 'leaderboard'), { params: cleanAnalyticsParams(params), signal }).then(response => response.data),

  getLateArrivals: (
    companyId: string,
    params: {
      period_start: string;
      period_end: string;
      department_id?: string;
      bucket_minutes: number;
    },
    signal?: AbortSignal
  ): Promise<AnalyticsLateArrivalsResponse> =>
    apiClient.get(analyticsPath(companyId, 'late-arrivals'), { params: cleanAnalyticsParams(params), signal }).then(response => response.data),

  exportPdf: (
    companyId: string,
    params: {
      period_type: AnalyticsPeriodType;
      period_start: string;
      department_id?: string;
    },
    signal?: AbortSignal
  ): Promise<{
    success: boolean;
    async?: boolean;
    job_id: string;
    message?: string;
  }> =>
    apiClient.get(analyticsPath(companyId, 'export-pdf'), {
      params: cleanAnalyticsParams(params),
      signal,
    }).then(response => response.data),

  checkExportPdfStatus: (
    companyId: string,
    jobId: string,
    signal?: AbortSignal
  ): Promise<ExportJobStatus> =>
    apiClient.get(analyticsPath(companyId, `export-pdf/status/${jobId}`), { signal }).then(response => response.data),
};

// Employee Timeline (per-day attendance sessions)
export const timelineAPI = {
  getTimeline: (companyId: string, dateOrParams?: string | TimelineParams, signal?: AbortSignal): Promise<{
    total?: number;
    last_page?: number;
    attendanceData: Array<{
      employee: string;
      department: string;
      check_in: string | null;
      check_out: string | null;
      actual_work_minutes: number | string;
      actual_work_hours: number | string;
      expected_minutes: number | string;
      expected_hours: number | string;
      status: string;
      sessions?: Array<{
        start: string;
        end: string;
        minutes: number;
        status: 'Green' | 'Red' | 'Gray';
      }>;
    }>;
  }> => {
    const params = queryParams(typeof dateOrParams === 'string' ? { date: dateOrParams } : dateOrParams ?? {});
    const qs = params.toString();
    return cachedListRequest(`timeline/${companyId}${qs ? `?${qs}` : ''}`,
      `timeline_${companyId}_${qs}`, 2, { signal });
  },
};

// Vacation API
export const vacationAPI = {
  // Get vacations for a specific employee
  getEmployeeVacations: async (employeeId: number, filters: VacationFilters = {}, signal?: AbortSignal): Promise<VacationListResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    
    if (filters.from_date) params.append('from_date', filters.from_date);
    if (filters.to_date) params.append('to_date', filters.to_date);
    if (filters.only_active) params.append('only_active', '1');
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `employee_vacations_${employeeId}_${queryString}`;
    
    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached employee vacations data for employee:', employeeId);
      return Promise.resolve(cachedData);
    }

    const url = `employees/${employeeId}/vacations${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);

    return cachedListRequest(url, cacheKey, 3, { signal });
  },

  // Create vacation for a specific employee
  createEmployeeVacation: (employeeId: number, vacationData: CreateVacationRequest): Promise<CreateVacationResponse> => {
    log('🌐 API: Making POST request to /employees/' + employeeId + '/vacations');
    log('🌐 API: Request payload:', vacationData);
    
    return apiClient.post(`employees/${employeeId}/vacations`, vacationData).then(response => {
      log('🌐 API: POST /employees/' + employeeId + '/vacations - Status:', response.status);
      log('🌐 API: POST /employees/' + employeeId + '/vacations - Response:', response.data);
      
      // Clear vacation cache for this employee and company
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employee_vacations_${employeeId}_`);
      if (response.data.success !== false && response.data.success !== 0) clearCache(`company_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /employees/' + employeeId + '/vacations - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Update vacation for a specific employee
  updateEmployeeVacation: (employeeId: number, vacationId: number, vacationData: UpdateVacationRequest): Promise<UpdateVacationResponse> => {
    log('🌐 API: Making PUT request to /employees/' + employeeId + '/vacations/' + vacationId);
    log('🌐 API: Request payload:', vacationData);
    
    return apiClient.put(`employees/${employeeId}/vacations/${vacationId}`, vacationData).then(response => {
      log('🌐 API: PUT /employees/' + employeeId + '/vacations/' + vacationId + ' - Status:', response.status);
      log('🌐 API: PUT /employees/' + employeeId + '/vacations/' + vacationId + ' - Response:', response.data);
      
      // Clear vacation cache for this employee and company
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employee_vacations_${employeeId}_`);
      if (response.data.success !== false && response.data.success !== 0) clearCache(`company_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /employees/' + employeeId + '/vacations/' + vacationId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Delete vacation for a specific employee
  deleteEmployeeVacation: (employeeId: number, vacationId: number): Promise<DeleteVacationResponse> => {
    log('🌐 API: Making DELETE request to /employees/' + employeeId + '/vacations/' + vacationId);
    
    return apiClient.delete(`employees/${employeeId}/vacations/${vacationId}`).then(response => {
      log('🌐 API: DELETE /employees/' + employeeId + '/vacations/' + vacationId + ' - Status:', response.status);
      log('🌐 API: DELETE /employees/' + employeeId + '/vacations/' + vacationId + ' - Response:', response.data);
      
      // Clear vacation cache for this employee and company
      if (response.data.success !== false && response.data.success !== 0) clearCache(`employee_vacations_${employeeId}_`);
      if (response.data.success !== false && response.data.success !== 0) clearCache(`company_vacations_`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /employees/' + employeeId + '/vacations/' + vacationId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Get company vacations
  getCompanyVacations: async (companyId: string, filters: CompanyVacationFilters, signal?: AbortSignal): Promise<VacationListWithEmployeeResponse> => {
    throwIfCanceled(signal);
    // Build query parameters
    const params = new URLSearchParams();
    
    // Required parameters
    params.append('from_date', filters.from_date);
    params.append('to_date', filters.to_date);
    
    // Optional parameters
    if (filters.employee_id) params.append('employee_id', filters.employee_id.toString());
    if (filters.only_active) params.append('only_active', '1');
    if (filters.include_employee !== undefined) params.append('include_employee', filters.include_employee.toString());
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    
    const queryString = params.toString();
    const cacheKey = `company_vacations_${companyId}_${queryString}`;
    
    // Check cache first
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached company vacations data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `companies/${companyId}/vacations?${queryString}`;
    log('🌐 API: Making GET request to /' + url);

    return cachedListRequest(url, cacheKey, 3, { signal });
  },
};

export const holidayAPI = {
  getHolidays: (companyId: string): Promise<HolidayListResponse> => {
    log('🌐 API: Making GET request to /holidays/' + companyId);
    return apiClient.get(`holidays/${companyId}`).then(response => response.data);
  },

  createHoliday: (data: CreateHolidayRequest): Promise<CreateHolidayResponse> => {
    log('🌐 API: Making POST request to /create-holiday');
    return apiClient.post('create-holiday', data).then(response => response.data);
  },

  updateHoliday: (id: number, data: UpdateHolidayRequest): Promise<UpdateHolidayResponse> => {
    log('🌐 API: Making PUT request to /holidays/' + id);
    return apiClient.put(`holidays/${id}`, data).then(response => response.data);
  },

  deleteHoliday: (id: number): Promise<DeleteHolidayResponse> => {
    log('🌐 API: Making DELETE request to /holidays/' + id);
    return apiClient.delete(`holidays/${id}`).then(response => response.data);
  },
};

// Policy API
export const policyAPI = {
  // Get all policies for a company
  getPolicies: (companyId: string, filters: PolicyFilters = {}, signal?: AbortSignal): Promise<PoliciesAPIResponse> => {
    const params = new URLSearchParams();
    if (filters.name) params.append('name', filters.name);
    if (filters.is_enabled !== undefined) params.append('is_enabled', filters.is_enabled.toString());
    if (filters.alert_level) params.append('alert_level', filters.alert_level);

    const queryString = params.toString();
    const cacheKey = `policies_${companyId}_${queryString}`;

    const url = `companies/${companyId}/policies${queryString ? `?${queryString}` : ''}`;
    return cachedListRequest(url, cacheKey, 5, { signal });
  },
  // Create a new policy
  createPolicy: (policyData: CreatePolicyRequest): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making POST request to /policies');
    log('🌐 API: POST /policies - Request data:', policyData);

    return apiClient.post('policies', policyData).then(response => {
      log('🌐 API: POST /policies - Status:', response.status);
      log('🌐 API: POST /policies - Response:', response.data);
      
      // Clear policies cache for this company
      clearCache(`policies_${policyData.company_id}`);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /policies - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Update a policy
  updatePolicy: (policyId: number, policyData: Omit<CreatePolicyRequest, 'company_id'>): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making PUT request to /policies/' + policyId);
    log('🌐 API: PUT /policies/' + policyId + ' - Request data:', policyData);

    return apiClient.put(`policies/${policyId}`, policyData).then(response => {
      log('🌐 API: PUT /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: PUT /policies/' + policyId + ' - Response:', response.data);
      
      // Clear all policies cache
      clearCache('policies_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      logError('🌐 API: Error details:', error.response?.data?.errors);
      logError('🌐 API: Full error response:', JSON.stringify(error.response?.data, null, 2));
      throw error;
    });
  },

  // Get a single policy
  getPolicy: (policyId: number): Promise<CreatePolicyResponse> => {
    log('🌐 API: Making GET request to /policies/' + policyId);

    return apiClient.get(`policies/${policyId}`).then(response => {
      log('🌐 API: GET /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: GET /policies/' + policyId + ' - Response:', response.data);
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // Delete a policy
  deletePolicy: (policyId: number): Promise<{ success: boolean; message: string }> => {
    log('🌐 API: Making DELETE request to /policies/' + policyId);

    return apiClient.delete(`policies/${policyId}`).then(response => {
      log('🌐 API: DELETE /policies/' + policyId + ' - Status:', response.status);
      log('🌐 API: DELETE /policies/' + policyId + ' - Response:', response.data);
      
      // Clear all policies cache
      clearCache('policies_');
      
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /policies/' + policyId + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  }
};

// ===========================
// NOTIFICATION API
// ===========================

export const notificationAPI = {
  /**
   * Get notifications for a user in a company
   * GET /api/notifications/{company}
   * Supports pagination with page and per_page parameters
   */
  async getNotifications(company: string, userId: number, filters: NotificationFilters = {}, signal?: AbortSignal): Promise<NotificationsListResponse> {
    log(`🔔 API: GET /notifications/${company} (user_id=${userId}, filters=${JSON.stringify(filters)})`);
    
    const params: any = { user_id: userId };
    if (filters.unread_only) {
      params.unread_only = true;
    }
    if (filters.per_page) {
      params.per_page = filters.per_page;
    }
    if (filters.page) {
      params.page = filters.page;
    }
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    if (filters.action_type) {
      params.action_type = filters.action_type;
    }
    
    // Notifications can be slow with large datasets - increase timeout to 45 seconds
    return apiClient.get(`notifications/${company}`, {
      params,
      timeout: 45000,
      signal,
    }).then(response => {
      log('🔔 API: GET /notifications/' + company + ' - Success');
      return response.data;
    }).catch(error => {
      logError('🔔 API: GET /notifications/' + company + ' - Error:', error);
      throw error;
    });
  },

  /**
   * Get unread notification count for a user in a company
   * GET /api/notifications/unread-count/{company}/{user_id}
   */
  async getUnreadCount(company: string, userId: number, signal?: AbortSignal): Promise<UnreadCountResponse> {
    log(`🔔 API: GET /notifications/unread-count/${company}/${userId}`);
    
    return apiClient.get(`notifications/unread-count/${company}/${userId}`, { signal }).then(response => {
      log('🔔 API: GET /notifications/unread-count - Success:', response.data.data.unread_count);
      return response.data;
    }).catch(error => {
      logError('🔔 API: GET /notifications/unread-count - Error:', error);
      throw error;
    });
  },

  /**
   * Mark single notification as read
   * PUT /api/notifications/{id}/mark-read
   */
  async markAsRead(notificationId: number): Promise<MarkReadResponse> {
    log(`🔔 API: PUT /notifications/${notificationId}/mark-read`);
    
    return apiClient.put(`notifications/${notificationId}/mark-read`, {}).then(response => {
      log('🔔 API: PUT /notifications/mark-read - Success');
      return response.data;
    }).catch(error => {
      logError('🔔 API: PUT /notifications/mark-read - Error:', error);
      throw error;
    });
  },

  /**
   * Mark all user's notifications as read in a company
   * PUT /api/notifications/mark-all-read/{company}
   */
  async markAllAsRead(company: string, userId: number): Promise<MarkAllReadResponse> {
    log(`🔔 API: PUT /notifications/mark-all-read/${company} (user_id=${userId})`);
    
    return apiClient.put(`notifications/mark-all-read/${company}`, {
      user_id: userId
    }).then(response => {
      log('🔔 API: PUT /notifications/mark-all-read - Success:', response.data.data.updated_count + ' notifications marked as read');
      return response.data;
    }).catch(error => {
      logError('🔔 API: PUT /notifications/mark-all-read - Error:', error);
      throw error;
    });
  },

  async acknowledgeNotification(notificationId: number): Promise<AcknowledgeResponse> {
    log(`ðŸ”” API: PUT /notifications/${notificationId}/acknowledge`);
    return apiClient.put(`notifications/${notificationId}/acknowledge`, {}).then(response => response.data);
  },

  async resolveNotification(notificationId: number, note?: string): Promise<ResolveResponse> {
    log(`ðŸ”” API: PUT /notifications/${notificationId}/resolve`);
    return apiClient.put(`notifications/${notificationId}/resolve`, { resolution_note: note }).then(response => response.data);
  }
};

export const deviceTokenAPI = {
  registerToken: (data: DeviceTokenRequest): Promise<{ success: boolean; message?: string }> => {
    return apiClient.post('device-tokens', data).then(response => response.data);
  },
  unregisterToken: (data: { token: string }): Promise<{ success: boolean; message?: string }> => {
    return apiClient.delete('device-tokens', { data }).then(response => response.data);
  },
};

interface CompanyForCreation {
  mode?: 'attach' | 'create';
  company_id: string;
  name?: string;
  status?: 1 | 2;
  email?: string;
  phone?: string;
  address?: string;
  description?: string;
  timezone?: string;
  valid_th_from?: number;
  valid_th_to?: number;
  repo_th?: number;
  similarity_addition?: number;
  ppe_types?: string[] | null;
  ppe_min_confidence?: number;
  shifts_enabled?: boolean | null;
  shift_auto_detect?: boolean | null;
}

interface LocationForCreation {
    name: string;
    description?: string;
    address?: string;
    timezone?: string;
    is_active?: boolean;
    repo_th?: number;
    valid_th_from?: number;
    valid_th_to?: number;
    similarity_addition?: number;
    ppe_types?: string[] | null;
    ppe_min_confidence?: number;
    shifts_enabled?: boolean | null;
    shift_auto_detect?: boolean | null;
}

export const ppeDepartmentSettingsAPI = {
  listDepartmentPpeRequirements: (companyId: string): Promise<PPEDepartmentRequirementsResponse> => {
    const url = `companies/${companyId}/departments/ppe-requirements`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => response.data);
  },

  getDepartmentPpeRequirements: (companyId: string, departmentId: number): Promise<{ success: boolean; data: PPEDepartmentRequirement }> => {
    const url = `companies/${companyId}/departments/${departmentId}/ppe-requirements`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => response.data);
  },

  updateDepartmentPpeRequirements: (companyId: string, departmentId: number, body: { required_ppe_types: string[] | null; strict_mode: boolean; ppe_status: boolean }): Promise<{ success: boolean; message: string }> => {
    const url = `companies/${companyId}/departments/${departmentId}/ppe-requirements`;
    log('🌐 API: Making PUT request to /' + url, body);
    return apiClient.put(url, body).then(response => response.data);
  },
};

export const ppeViolationsAPI = {
  getDailyViolations: (companyId: string, params: any, signal?: AbortSignal): Promise<PPEViolationsResponse> => {
    const queryString = new URLSearchParams(params).toString();
    const url = `companies/${companyId}/ppe/violations${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url, { signal }).then(response => response.data);
  },

  getMonthlySummary: (companyId: string, params: any, signal?: AbortSignal): Promise<PPEMonthlySummaryResponse> => {
    const queryString = new URLSearchParams(params).toString();
    const url = `companies/${companyId}/ppe/violations/monthly-summary${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url, { signal }).then(response => response.data);
  },

  getEmployeeViolationHistory: (companyId: string, employeeId: string, params: any, signal?: AbortSignal): Promise<PPEEmployeeViolationHistoryResponse> => {
    const queryString = new URLSearchParams(params).toString();
    const url = `companies/${companyId}/ppe/violations/employee/${employeeId}${queryString ? `?${queryString}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url, { signal }).then(response => response.data);
  },
};

export interface CompanyGroupCreationData {
    name: string;
    description?: string;
    is_active?: boolean;
    timezone?: string;
    address?: string;
    repo_th?: number;
    valid_th_from?: number;
    valid_th_to?: number;
    similarity_addition?: number;
    ppe_types?: string[] | null;
    location?: LocationForCreation;
    company?: CompanyForCreation;
    ppe_min_confidence?: number;
    shifts_enabled?: boolean | null;
    shift_auto_detect?: boolean | null;
    payroll_enabled?: boolean | null;
}

export interface CompanyLocationCreationData {
    name: string;
    description?: string;
    address?: string;
    timezone?: string;
    is_active?: boolean;
    repo_th?: number;
    valid_th_from?: number;
    valid_th_to?: number;
    similarity_addition?: number;
    ppe_types?: string[] | null;
    ppe_min_confidence?: number;
    shifts_enabled?: boolean | null;
    shift_auto_detect?: boolean | null;
    payroll_enabled?: boolean | null;
    company?: CompanyForCreation;
}

export const createCompanyGroup = async (data: CompanyGroupCreationData) => {
  const response = await apiClient.post('/companies/groups', data);
  clearCache('groups_');
  clearCache('locations_');
  clearCache('companies');
  return response.data;
};

export const createCompanyLocation = async (companyGroupId: string | number, data: CompanyLocationCreationData) => {
  const response = await apiClient.post(`/groups/${companyGroupId}/locations`, data);
  clearCache(`locations_group_${companyGroupId}`);
  clearCache('groups_');
  clearCache('companies');
  return response.data;
};

export const shiftAPI = {
  // --- Shift Templates ---
  getShiftTemplates: (companyId: string, includeInactive: boolean = false): Promise<ShiftTemplateListResponse> => {
    const cacheKey = `shift_templates_${companyId}_${includeInactive}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached shift templates for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `companies/${companyId}/shifts/templates${includeInactive ? '?include_inactive=1' : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    }).catch(error => {
      if (error.response?.status === 404) {
        const empty: ShiftTemplateListResponse = { success: true, data: [] };
        setCachedData(cacheKey, empty, 5);
        return empty;
      }
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createShiftTemplate: (data: CreateShiftTemplateRequest): Promise<{ success: boolean; message?: string; data?: ShiftTemplate }> => {
    const url = `companies/${data.company_id}/shifts/templates`;
    log('🌐 API: Making POST request to /' + url);
    return apiClient.post(url, data).then(response => {
      log('🌐 API: POST /' + url + ' - Status:', response.status);
      log('🌐 API: POST /' + url + ' - Response:', response.data);
      clearCache(`shift_templates_${data.company_id}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateShiftTemplate: (companyId: string, templateId: number, data: UpdateShiftTemplateRequest): Promise<{ success: boolean; message?: string; data?: ShiftTemplate }> => {
    const url = `companies/${companyId}/shifts/templates/${templateId}`;
    log('🌐 API: Making PUT request to /' + url);
    return apiClient.put(url, data).then(response => {
      log('🌐 API: PUT /' + url + ' - Status:', response.status);
      log('🌐 API: PUT /' + url + ' - Response:', response.data);
      clearCache(`shift_templates_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // --- Shift Assignments ---
  getAssignments: (companyId: string, params?: { employee_id?: number; date_from?: string; date_to?: string }): Promise<ShiftAssignmentListResponse> => {
    const qs = new URLSearchParams();
    if (params?.employee_id) qs.append('employee_id', String(params.employee_id));
    if (params?.date_from) qs.append('date_from', params.date_from);
    if (params?.date_to) qs.append('date_to', params.date_to);
    const url = `companies/${companyId}/shifts/assignments${qs.toString() ? `?${qs}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createAssignment: (data: CreateShiftAssignmentRequest): Promise<{ success: boolean; message?: string; data?: ShiftAssignment }> => {
    const url = `companies/${data.company_id}/shifts/assignments`;
    log('🌐 API: Making POST request to /' + url);
    return apiClient.post(url, data).then(response => {
      log('🌐 API: POST /' + url + ' - Status:', response.status);
      log('🌐 API: POST /' + url + ' - Response:', response.data);
      clearCache(`shift_schedule_${data.company_id}`);
      clearCache(`shift_staffing_${data.company_id}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  bulkAssign: (data: BulkShiftAssignmentRequest): Promise<{ success: boolean; message?: string }> => {
    const url = `companies/${data.company_id}/shifts/assignments/bulk`;
    log('🌐 API: Making POST request to /' + url);
    return apiClient.post(url, data).then(response => {
      log('🌐 API: POST /' + url + ' - Status:', response.status);
      log('🌐 API: POST /' + url + ' - Response:', response.data);
      clearCache(`shift_schedule_${data.company_id}`);
      clearCache(`shift_staffing_${data.company_id}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateAssignment: (companyId: string, assignmentId: number, data: UpdateShiftAssignmentRequest): Promise<{ success: boolean; message?: string; data?: ShiftAssignment }> => {
    const url = `companies/${companyId}/shifts/assignments/${assignmentId}`;
    log('🌐 API: Making PUT request to /' + url);
    return apiClient.put(url, data).then(response => {
      clearCache(`shift_schedule_${companyId}`);
      clearCache(`shift_staffing_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /' + url + ' - Error:', error);
      throw error;
    });
  },

  removeAssignment: (companyId: string, assignmentId: number): Promise<{ success: boolean; message?: string }> => {
    const url = `companies/${companyId}/shifts/assignments/${assignmentId}`;
    log('🌐 API: Making DELETE request to /' + url);
    return apiClient.delete(url).then(response => {
      log('🌐 API: DELETE /' + url + ' - Status:', response.status);
      log('🌐 API: DELETE /' + url + ' - Response:', response.data);
      clearCache(`shift_schedule_${companyId}`);
      clearCache(`shift_staffing_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: DELETE /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // --- Rotation Groups ---
  getRotationGroups: (companyId: string): Promise<RotationGroupListResponse> => {
    const cacheKey = `shift_rotations_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached rotation groups for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `companies/${companyId}/shifts/rotations`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 5);
      return response.data;
    }).catch(error => {
      if (error.response?.status === 404) {
        const empty: RotationGroupListResponse = { success: true, data: [] };
        setCachedData(cacheKey, empty, 5);
        return empty;
      }
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  createRotationGroup: (data: CreateRotationGroupRequest): Promise<{ success: boolean; message?: string; data?: ShiftRotationGroup }> => {
    const url = `companies/${data.company_id}/shifts/rotations`;
    log('🌐 API: Making POST request to /' + url);
    return apiClient.post(url, data).then(response => {
      log('🌐 API: POST /' + url + ' - Status:', response.status);
      log('🌐 API: POST /' + url + ' - Response:', response.data);
      clearCache(`shift_rotations_${data.company_id}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  updateRotationGroup: (companyId: string, rotationId: number, data: UpdateRotationGroupRequest): Promise<{ success: boolean; message?: string; data?: ShiftRotationGroup }> => {
    const url = `companies/${companyId}/shifts/rotations/${rotationId}`;
    log('🌐 API: Making PUT request to /' + url);
    return apiClient.put(url, data).then(response => {
      log('🌐 API: PUT /' + url + ' - Status:', response.status);
      log('🌐 API: PUT /' + url + ' - Response:', response.data);
      clearCache(`shift_rotations_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  generateRotation: (companyId: string, rotationId: number, data: GenerateRotationRequest): Promise<{ success: boolean; message?: string }> => {
    const url = `companies/${companyId}/shifts/rotations/${rotationId}/generate`;
    log('🌐 API: Making POST request to /' + url);
    return apiClient.post(url, data).then(response => {
      log('🌐 API: POST /' + url + ' - Status:', response.status);
      log('🌐 API: POST /' + url + ' - Response:', response.data);
      clearCache(`shift_schedule_${companyId}`);
      clearCache(`shift_staffing_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: POST /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // --- Swap Requests (Admin) ---
  getSwapRequests: (companyId: string, params?: { status?: string; date_from?: string; date_to?: string; employee_search?: string; page?: number; per_page?: number }): Promise<SwapRequestListResponse> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.append('status', params.status);
    if (params?.date_from) qs.append('date_from', params.date_from);
    if (params?.date_to) qs.append('date_to', params.date_to);
    if (params?.employee_search) qs.append('employee_search', params.employee_search);
    if (params?.page) qs.append('page', String(params.page));
    if (params?.per_page) qs.append('per_page', String(params.per_page));
    const url = `companies/${companyId}/shifts/swaps${qs.toString() ? `?${qs}` : ''}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      return response.data;
    }).catch(error => {
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  reviewSwap: (companyId: string, swapId: number, data: ReviewSwapRequest): Promise<{ success: boolean; message?: string; data?: ShiftSwapRequest }> => {
    const url = `companies/${companyId}/shifts/swaps/${swapId}/review`;
    log('🌐 API: Making PUT request to /' + url);
    return apiClient.put(url, data).then(response => {
      log('🌐 API: PUT /' + url + ' - Status:', response.status);
      log('🌐 API: PUT /' + url + ' - Response:', response.data);
      clearCache(`shift_schedule_${companyId}`);
      clearCache(`shift_staffing_${companyId}`);
      return response.data;
    }).catch(error => {
      logError('🌐 API: PUT /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // --- Schedule View ---
  getSchedule: (companyId: string, params: { date_from: string; date_to: string; department_id?: number | null }): Promise<ScheduleResponse> => {
    const qs = new URLSearchParams();
    qs.append('date_from', params.date_from);
    qs.append('date_to', params.date_to);
    if (params.department_id) qs.append('department_id', String(params.department_id));
    const cacheKey = `shift_schedule_${companyId}_${qs.toString()}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached schedule data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `companies/${companyId}/shifts/schedule?${qs}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 2);
      return response.data;
    }).catch(error => {
      if (error.response?.status === 404) {
        const empty: ScheduleResponse = {
          success: true,
          data: { date_from: '', date_to: '', dates: [], employees: [] },
        };
        return empty;
      }
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },

  // --- Staffing ---
  getStaffing: (companyId: string, params: { date_from: string; date_to: string; department_id?: number | null }): Promise<StaffingResponse> => {
    const qs = new URLSearchParams();
    qs.append('date_from', params.date_from);
    qs.append('date_to', params.date_to);
    if (params.department_id) qs.append('department_id', String(params.department_id));
    const cacheKey = `shift_staffing_${companyId}_${qs.toString()}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) {
      log('💾 Using cached staffing data for company:', companyId);
      return Promise.resolve(cachedData);
    }

    const url = `companies/${companyId}/shifts/staffing?${qs}`;
    log('🌐 API: Making GET request to /' + url);
    return apiClient.get(url).then(response => {
      log('🌐 API: GET /' + url + ' - Status:', response.status);
      log('🌐 API: GET /' + url + ' - Response:', response.data);
      setCachedData(cacheKey, response.data, 2);
      return response.data;
    }).catch(error => {
      if (error.response?.status === 404) {
        const empty: StaffingResponse = { success: true, data: [] };
        return empty;
      }
      logError('🌐 API: GET /' + url + ' - Error:', error);
      logError('🌐 API: Error status:', error.response?.status);
      logError('🌐 API: Error data:', error.response?.data);
      throw error;
    });
  },
};

// Clear cache function for manual cache invalidation
export const clearCache = (pattern?: string) => {
  sharedListRequests.forEach((_request, key) => {
    if (!pattern || key.includes(pattern)) sharedListRequests.delete(key);
  });
  pendingCacheReads.forEach((reads, key) => {
    if (!pattern || key.includes(pattern)) reads.forEach(read => { read.invalidated = true; });
  });
  if (pattern) {
    const keys = Array.from(cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        cache.delete(key);
      }
    });
  } else {
    cache.clear();
  }
};

export const customReportAPI = {
  getSchema: (companyId: string, signal?: AbortSignal): Promise<ReportSchema> => {
    const cacheKey = `report_schema_${companyId}`;
    const cachedData = getCachedData(cacheKey);
    if (cachedData) return Promise.resolve(cachedData);

    return apiClient.get(`/companies/${companyId}/reports/schema`, { signal }).then(response => {
      setCachedData(cacheKey, response.data, 60); // Cache schema for 1 hour
      return response.data;
    });
  },
  
  preview: (companyId: string, params: ReportPreviewRequest, signal?: AbortSignal): Promise<ReportPreviewResponse> => {
    return apiClient.post(`/companies/${companyId}/reports/preview`, params, { signal }).then(response => response.data);
  },
  
  list: (companyId: string, params?: { search?: string; page?: number; per_page?: number }): Promise<ListSavedReportsResponse> => {
    return apiClient.get(`/companies/${companyId}/reports`, { params }).then(response => response.data);
  },
  
  get: (companyId: string, reportId: number, signal?: AbortSignal): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.get(`/companies/${companyId}/reports/${reportId}`, { signal }).then(response => response.data);
  },
  
  create: (companyId: string, data: SaveReportRequest): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.post(`/companies/${companyId}/reports`, data).then(response => response.data);
  },
  
  update: (companyId: string, reportId: number, data: SaveReportRequest): Promise<{ success: boolean; data: SavedReport }> => {
    return apiClient.put(`/companies/${companyId}/reports/${reportId}`, data).then(response => response.data);
  },
  
  delete: (companyId: string, reportId: number): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete(`/companies/${companyId}/reports/${reportId}`).then(response => response.data);
  },
  
  export: (companyId: string, data: ExportReportRequest): Promise<Blob | { success: boolean; job_id: string }> => {
    const url = `companies/${companyId}/reports/export`;
    log('🌐 API: Making POST request to /' + url, data);
    return apiClient.post(url, data, {
      responseType: 'blob',
      timeout: 120000 
    }).then(response => {
      if (response.headers['content-type']?.includes('application/json')) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const json = JSON.parse(reader.result as string);
              if (json.success === false) {
                reject(new Error(json.message || 'Export failed'));
              } else {
                // Async export: backend may use job_id at root, under data, camelCase, or task_id — normalize for polling.
                const rawJobId =
                  json.job_id ??
                  json.data?.job_id ??
                  json.jobId ??
                  json.data?.jobId ??
                  json.task_id ??
                  json.data?.task_id;
                if (rawJobId != null && rawJobId !== '') {
                  resolve({ ...json, job_id: String(rawJobId) });
                } else {
                  resolve(json);
                }
              }
            } catch (e) {
              reject(new Error('Failed to parse JSON response'));
            }
          };
          reader.onerror = () => reject(new Error('Failed to read response blob'));
          reader.readAsText(response.data);
        });
      }
      return response.data;
    });
  },
  checkExportJob: (companyId: string, jobId: string): Promise<ExportJobStatus> => {
    return apiClient.get(`/companies/${companyId}/reports/exports/${jobId}`).then(response => response.data);
  },
  };

  export const snapshotSettingsAPI = {
    getSettings: (companyId: string): Promise<SnapshotSettingsResponse> => {
      return apiClient.get(`snapshot-settings/${companyId}`).then(r => r.data);
    },

    updateSettings: (companyId: string, data: UpdateSnapshotSettingsRequest): Promise<SnapshotSettingsResponse> => {
      return apiClient.put(`snapshot-settings/${companyId}`, data).then(r => r.data);
    },

    deleteSettings: (companyId: string): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshot-settings/${companyId}`).then(r => r.data);
    },
  };

  export const snapshotsAPI = {
    listSnapshots: (companyId: string, filters: SnapshotFilters = {}, signal?: AbortSignal): Promise<SnapshotsListResponse> => {
      const params = new URLSearchParams();
      if (filters.camera_id) params.append('camera_id', filters.camera_id.toString());
      if (filters.department_id) params.append('department_id', filters.department_id.toString());
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.analysis_status) params.append('analysis_status', filters.analysis_status);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      const qs = params.toString();
      return apiClient.get(`snapshots/${companyId}${qs ? `?${qs}` : ''}`, { signal }).then(r => r.data);
    },

    storeSnapshot: (data: FormData): Promise<StoreSnapshotResponse> => {
      return apiClient.post('snapshots/store', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 45000,
      }).then(r => r.data);
    },

    deleteSnapshot: (companyId: string, snapshotId: number): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshots/${companyId}/${snapshotId}`).then(r => r.data);
    },

    bulkDeleteSnapshots: (companyId: string, body: BulkDeleteSnapshotsRequest): Promise<SnapshotDeleteResponse> => {
      return apiClient.delete(`snapshots/${companyId}/bulk`, { data: body }).then(r => r.data);
    },
  };

export const analysisPromptAPI = {
  listPrompts: (companyId: string): Promise<AnalysisPromptsListResponse> => {
    return apiClient.get(`analysis-prompts/${companyId}`).then(r => r.data);
  },

  createPrompt: (companyId: string, data: CreateAnalysisPromptRequest): Promise<AnalysisPromptResponse> => {
    return apiClient.post(`analysis-prompts/${companyId}`, data).then(r => r.data);
  },

  updatePrompt: (companyId: string, promptId: number, data: UpdateAnalysisPromptRequest): Promise<AnalysisPromptResponse> => {
    return apiClient.put(`analysis-prompts/${companyId}/${promptId}`, data).then(r => r.data);
  },

  deletePrompt: (companyId: string, promptId: number): Promise<AnalysisPromptDeleteResponse> => {
    return apiClient.delete(`analysis-prompts/${companyId}/${promptId}`).then(r => r.data);
  },
};

export const snapshotAnalysisAPI = {
  analyze: (companyId: string, snapshotId: number, data: AnalyzeSnapshotRequest, signal?: AbortSignal): Promise<AnalyzeSnapshotResponse> => {
    return apiClient.post(`snapshots/${companyId}/${snapshotId}/analyze`, data, { signal })
      .then(r => r.status === 202 ? { ...r.data, httpStatus: 202 } : r.data);
  },

  getHistory: (companyId: string, snapshotId: number, signal?: AbortSignal): Promise<AnalysisHistoryResponse> => {
    return apiClient.get(`snapshots/${companyId}/${snapshotId}/analyses`, { signal }).then(r => r.data);
  },
};

export const cameraDeskZonesAPI = {
  getZones: (companyId: string, cameraId: number): Promise<DeskZonesResponse> => {
    return apiClient.get(`cameras/${companyId}/${cameraId}/desk-zones`).then(r => r.data);
  },

  updateZones: (companyId: string, cameraId: number, body: UpdateDeskZonesRequest): Promise<DeskZonesMutationResponse> => {
    return apiClient.put(`cameras/${companyId}/${cameraId}/desk-zones`, body).then(r => r.data);
  },

  deleteZone: (companyId: string, cameraId: number, deskKey: string): Promise<DeskZonesMutationResponse> => {
    return apiClient.delete(`cameras/${companyId}/${cameraId}/desk-zones/${encodeURIComponent(deskKey)}`).then(r => r.data);
  },
};

export const deskOccupancyAPI = {
  getState: (companyId: string, cameraId: number, signal?: AbortSignal): Promise<DeskOccupancyStateResponse> => {
    return apiClient.get(`cameras/${companyId}/${cameraId}/desk-occupancy/state`, { signal }).then(r => r.data);
  },

  getHistory: (companyId: string, cameraId: number, filters: DeskOccupancyHistoryFilters = {}, signal?: AbortSignal): Promise<DeskOccupancyHistoryResponse> => {
    const params = new URLSearchParams();
    if (filters.desk_key) params.append('desk_key', filters.desk_key);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.per_page) params.append('per_page', filters.per_page.toString());
    if (filters.page) params.append('page', filters.page.toString());
    const qs = params.toString();
    return apiClient.get(`cameras/${companyId}/${cameraId}/desk-occupancy/history${qs ? `?${qs}` : ''}`, { signal }).then(r => r.data);
  },
};

export const deskReportAPI = {
  getDaily: (companyId: string, cameraId: number, date: string): Promise<DeskDailyReportResponse> =>
    apiClient.get(`cameras/${companyId}/${cameraId}/desk-report/daily`, { params: { date } }).then(r => r.data),
};

export const deskActivityAPI = {
  getState: (companyId: string, cameraId: number): Promise<DeskActivityStateResponse> =>
    apiClient.get(`cameras/${companyId}/${cameraId}/desk-activity/state`).then(r => r.data),

  getHistory: (companyId: string, cameraId: number): Promise<DeskActivityHistoryResponse> =>
    apiClient.get(`cameras/${companyId}/${cameraId}/desk-activity/history`).then(r => r.data),

  getSummary: (companyId: string, cameraId: number, date: string): Promise<DeskActivitySummaryResponse> =>
    apiClient.get(`cameras/${companyId}/${cameraId}/desk-activity/summary`, { params: { date } }).then(r => r.data),
};

const payrollPath = (companyId: string, endpoint: string) =>
  `companies/${encodeURIComponent(companyId)}/payroll/${endpoint}`;

const payrollPaginator = <T>(data: any, map: (item: any) => T = item => item): any => {
  const paginator = data?.data;
  const items = Array.isArray(paginator) ? paginator : (paginator?.data ?? paginator?.items ?? []);
  return {
    ...data,
    data: {
      current_page: paginator?.current_page ?? 1,
      per_page: paginator?.per_page ?? items.length,
      last_page: paginator?.last_page ?? 1,
      total: paginator?.total ?? items.length,
      items: items.map(map),
    },
  };
};

const payrollPayslip = (item: any): any => ({
  ...item,
  period_id: item.period_id ?? item.payroll_period_id,
  emp_id: item.emp_id ?? item.employee_emp_id ?? '',
  position_snapshot: item.position_snapshot ?? item.position ?? null,
  period_start: item.period_start ?? item.period?.period_start,
  period_end: item.period_end ?? item.period?.period_end,
  basic: item.basic ?? item.basic_amount ?? 0,
  gross: item.gross ?? item.gross_amount ?? 0,
  exemptions_total: item.exemptions_total ?? item.total_exemptions ?? item.exemptions?.reduce?.((sum: number, line: any) => sum + Number(line.amount ?? 0), 0) ?? 0,
  taxable_income: item.taxable_income ?? item.taxable_amount ?? 0,
  tax_amount: item.tax_amount ?? item.total_tax ?? 0,
  tax_rate: item.tax_rate ?? item.tax_rate_used ?? null,
  deductions_total: item.deductions_total ?? item.total_deductions ?? 0,
  net: item.net ?? item.net_amount ?? 0,
  exemption_lines: item.exemption_lines ?? item.exemptions ?? [],
  deduction_lines: item.deduction_lines ?? item.deductions ?? [],
});

const payrollExportStatus = (response: any): PayrollExportJobStatus => response?.data ?? response;

const payrollException = (item: any): any => ({
  ...item,
  period_id: item.period_id ?? item.payroll_period_id ?? null,
  employee_name: item.employee_name ?? item.employee?.name ?? '',
  emp_id: item.emp_id ?? item.employee_emp_id ?? item.employee?.emp_id ?? '',
  department_id: item.department_id ?? item.employee?.department_id ?? null,
  department_name:
    item.department_name ??
    item.employee?.department_name ??
    item.employee?.department?.name ??
    (item.employee?.department_id ? `#${item.employee.department_id}` : ''),
  absence_reason_id: item.absence_reason_id ?? item.employee_absence_reason_id ?? null,
  review_resolution: item.review_resolution ?? item.resolution ?? null,
  reviewer_name: item.reviewer_name ?? item.reviewer?.name ?? null,
  review_adjustment_minutes:
    item.review_adjustment_minutes ?? item.adjustment_minutes ?? null,
});

export const payrollAPI = {
  getSettings: (companyId: string, signal?: AbortSignal): Promise<PayrollSettingsResponse> =>
    apiClient.get(payrollPath(companyId, 'settings'), { signal }).then(r => ({ ...r.data, data: { ...r.data.data, tax_brackets: r.data.data?.tax_brackets ?? [] } })),

  updateSettings: (companyId: string, body: UpdatePayrollSettingsRequest): Promise<{ success: boolean; message: string }> =>
    apiClient.put(payrollPath(companyId, 'settings'), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  updatePushSettings: (companyId: string, body: UpdatePushSettingsRequest): Promise<{ success: boolean; message: string }> =>
    apiClient.put(payrollPath(companyId, 'settings/push'), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getPayComponents: (companyId: string, page = 1, perPage = 50): Promise<PayComponentListResponse> =>
    apiClient.get(payrollPath(companyId, 'pay-components'), { params: { page, per_page: perPage } }).then(r => payrollPaginator(r.data)),

  createPayComponent: (companyId: string, body: CreatePayComponentRequest): Promise<PayComponentResponse> =>
    apiClient.post(payrollPath(companyId, 'pay-components'), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  updatePayComponent: (companyId: string, id: number, body: UpdatePayComponentRequest): Promise<PayComponentResponse> =>
    apiClient.put(payrollPath(companyId, `pay-components/${id}`), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  deletePayComponent: (companyId: string, id: number): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(payrollPath(companyId, `pay-components/${id}`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getEmployeePaySetup: (companyId: string, employeeId: number): Promise<EmployeePaySetupResponse> =>
    apiClient.get(payrollPath(companyId, `employees/${employeeId}/pay-setup`)).then(r => {
      const data = r.data.data;
      return { ...r.data, data: { ...data, employee_id: employeeId, bank: { bank_name: data.bank_name, iban: data.bank_iban, account_number: data.bank_account_number, routing_number: data.bank_routing_code, salary_pay_mode: data.salary_pay_mode }, components: (data.components ?? []).map((component: any) => ({ ...component, pay_component_id: component.payroll_pay_component_id ?? component.id, code: component.code ?? '', effective_to: component.effective_to ?? null, active: component.active ?? true })) } };
    }),

  updateEmployeePaySetup: (companyId: string, employeeId: number, body: UpdateEmployeePaySetupRequest): Promise<{ success: boolean; message: string }> =>
    apiClient.put(payrollPath(companyId, `employees/${employeeId}/pay-setup`), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getExceptions: (companyId: string, filters: PayrollExceptionFilters = {}): Promise<PayrollExceptionListResponse> => {
    const params = new URLSearchParams();
    if (filters.period_id) params.append('period_id', String(filters.period_id));
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.employee_id) params.append('employee_id', String(filters.employee_id));
    if (filters.department_id) params.append('department_id', String(filters.department_id));
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    if (filters.per_page) params.append('per_page', String(filters.per_page));
    if (filters.page) params.append('page', String(filters.page));
    const qs = params.toString();
    return apiClient.get(payrollPath(companyId, `exceptions${qs ? `?${qs}` : ''}`)).then(r => payrollPaginator(r.data, payrollException));
  },

  getExceptionsSummary: (companyId: string): Promise<PayrollExceptionsSummaryResponse> =>
    apiClient.get(payrollPath(companyId, 'exceptions/summary')).then(r => {
      const rows = Array.isArray(r.data.data) ? r.data.data : [];
      const summary: any = { total: 0, open: 0, explained: 0, approved: 0, rejected: 0, ignored: 0, blocking: 0, warning: 0, by_type: {}, by_severity: {} };
      for (const row of rows) { const count = Number(row.count ?? 0); summary.total += count; summary[row.status] = (summary[row.status] ?? 0) + count; summary[row.severity] = (summary[row.severity] ?? 0) + count; summary.by_severity[row.severity] = (summary.by_severity[row.severity] ?? 0) + count; summary.by_type[row.type] = (summary.by_type[row.type] ?? 0) + count; }
      return { ...r.data, data: summary };
    }),

  reviewException: (companyId: string, id: number, body: ReviewExceptionRequest): Promise<{ success: boolean; message: string }> =>
    apiClient.put(payrollPath(companyId, `exceptions/${id}/review`), { ...body, note: body.review_note, review_note: undefined }).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  recomputeExceptions: (companyId: string, body: RecomputeExceptionsRequest): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, 'exceptions/recompute'), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getPeriods: (companyId: string, filters: PayrollPeriodFilters = {}, signal?: AbortSignal): Promise<PayrollPeriodListResponse> => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.per_page) params.append('per_page', String(filters.per_page));
    if (filters.page) params.append('page', String(filters.page));
    const qs = params.toString();
    return apiClient.get(payrollPath(companyId, `periods${qs ? `?${qs}` : ''}`), { signal }).then(r => payrollPaginator(r.data));
  },

  createPeriod: (companyId: string, body: CreatePeriodRequest): Promise<PayrollPeriodResponse> =>
    apiClient.post(payrollPath(companyId, 'periods'), body).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getPeriod: (companyId: string, periodId: number, signal?: AbortSignal): Promise<PayrollPeriodResponse> =>
    apiClient.get(payrollPath(companyId, `periods/${periodId}`), { signal }).then(r => r.data),

  recomputePeriod: (companyId: string, periodId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, `periods/${periodId}/recompute`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  lockPeriod: (companyId: string, periodId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, `periods/${periodId}/lock`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  reopenPeriod: (companyId: string, periodId: number, reason: string): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, `periods/${periodId}/reopen`), { reason }).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  deletePeriod: (companyId: string, periodId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.delete(payrollPath(companyId, `periods/${periodId}`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getPeriodAudit: (companyId: string, periodId: number, page = 1, perPage = 50, signal?: AbortSignal): Promise<PayrollAuditLogResponse> =>
    apiClient.get(payrollPath(companyId, `periods/${periodId}/audit`), { params: { page, per_page: perPage }, signal }).then(r => r.data),

  getPeriodTimesheets: (companyId: string, periodId: number, filters: PayrollTimesheetFilters = {}, signal?: AbortSignal): Promise<PayrollTimesheetListResponse> => {
    const params = new URLSearchParams();
    if (filters.employee_id) params.append('employee_id', String(filters.employee_id));
    if (filters.department_id) params.append('department_id', String(filters.department_id));
    if (filters.search) params.append('search', filters.search);
    if (filters.per_page) params.append('per_page', String(filters.per_page));
    if (filters.page) params.append('page', String(filters.page));
    const qs = params.toString();
    return apiClient.get(payrollPath(companyId, `periods/${periodId}/timesheets${qs ? `?${qs}` : ''}`), { signal }).then(r => payrollPaginator(r.data, item => ({ ...item, emp_id: item.emp_id ?? item.employee_emp_id ?? '', exception_count: item.exception_count ?? Number(item.blocking_exception_count ?? 0) + Number(item.warning_exception_count ?? 0) })));
  },

  getEmployeeTimesheet: (companyId: string, periodId: number, employeeId: number, signal?: AbortSignal): Promise<{ success: boolean; data: PayrollTimesheet }> =>
    apiClient.get(payrollPath(companyId, `periods/${periodId}/timesheets/${employeeId}`), { signal }).then(r => r.data),

  getPeriodPayslips: (companyId: string, periodId: number, filters: PayrollPayslipFilters = {}, signal?: AbortSignal): Promise<PayrollPayslipListResponse> => {
    const params = new URLSearchParams();
    if (filters.employee_id) params.append('employee_id', String(filters.employee_id));
    if (filters.department_id) params.append('department_id', String(filters.department_id));
    if (filters.search) params.append('search', filters.search);
    if (filters.per_page) params.append('per_page', String(filters.per_page));
    if (filters.page) params.append('page', String(filters.page));
    const qs = params.toString();
    return apiClient.get(payrollPath(companyId, `periods/${periodId}/payslips${qs ? `?${qs}` : ''}`), { signal }).then(r => payrollPaginator(r.data, payrollPayslip));
  },

  getPayslip: (companyId: string, payslipId: number): Promise<PayrollPayslipResponse> =>
    apiClient.get(payrollPath(companyId, `payslips/${payslipId}`)).then(r => ({ ...r.data, data: payrollPayslip(r.data.data) })),

  createPeriodExport: (companyId: string, periodId: number, type: PayrollExportType, signal?: AbortSignal): Promise<PayrollExportStartResponse> =>
    apiClient.post(payrollPath(companyId, `periods/${periodId}/exports`), { type }, { signal }).then(r => r.data),

  createPayslipExport: (companyId: string, payslipId: number, signal?: AbortSignal): Promise<PayrollExportStartResponse> =>
    apiClient.post(payrollPath(companyId, `payslips/${payslipId}/export`), undefined, { signal }).then(r => r.data),

  getExportStatus: (companyId: string, jobId: string, signal?: AbortSignal): Promise<PayrollExportJobStatus> =>
    apiClient.get(payrollPath(companyId, `exports/${jobId}`), { signal }).then(r => payrollExportStatus(r.data)),

  pushPeriod: (companyId: string, periodId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, `periods/${periodId}/push`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getPushDeliveries: (companyId: string, periodId: number, page = 1, perPage = 50, signal?: AbortSignal): Promise<PayrollPushDeliveryListResponse> =>
    apiClient.get(payrollPath(companyId, `periods/${periodId}/push-deliveries`), { params: { page, per_page: perPage }, signal }).then(r => payrollPaginator(r.data, item => ({ ...item, period_id: periodId, url: item.url ?? item.endpoint_url, created_at: item.created_at ?? item.last_attempt_at ?? '', updated_at: item.updated_at ?? item.last_attempt_at ?? '' }))),

  retryPushDelivery: (companyId: string, deliveryId: number): Promise<{ success: boolean; message: string }> =>
    apiClient.post(payrollPath(companyId, `push-deliveries/${deliveryId}/retry`)).then(r => r.data).then(data => { clearCache('payroll_' + companyId); return data; }),

  getOverview: (): Promise<PayrollOverviewResponse> =>
    apiClient.get('payroll/overview').then(r => ({ ...r.data, data: (r.data.data?.companies ?? []).map((company: any) => ({ company_id: company.id, company_name: company.name, latest_period_id: company.latest_period?.id ?? null, latest_period_start: company.latest_period?.start ?? null, latest_period_end: company.latest_period?.end ?? null, latest_period_status: company.latest_period?.status ?? null, locked_at: company.latest_period?.locked_at ?? null, total_net: company.total_net ?? null, currency: company.currency ?? null, timezone: company.timezone ?? null, employee_count: company.employee_count ?? null, open_blocking_count: company.open_blocking_exceptions ?? 0 })) })),
};

export const employeePayrollAPI = {
  getMyPayslips: (page = 1, perPage = 20, signal?: AbortSignal): Promise<EmployeePortalPayslipsResponse> =>
    employeeApiClient.get('employee/payslips', { params: { page, per_page: perPage }, signal }).then(r => payrollPaginator(r.data, payrollPayslip)),

  getMyPayslip: (id: number, signal?: AbortSignal): Promise<EmployeePortalPayslipResponse> =>
    employeeApiClient.get(`employee/payslips/${id}`, { signal }).then(r => ({ ...r.data, data: payrollPayslip(r.data.data) })),

  exportMyPayslip: (id: number, signal?: AbortSignal): Promise<PayrollExportStartResponse> =>
    employeeApiClient.post(`employee/payslips/${id}/export`, undefined, { signal }).then(r => r.data),

  getMyPayrollExceptions: (page = 1, perPage = 20): Promise<EmployeePortalExceptionsResponse> =>
    employeeApiClient.get('employee/payroll-exceptions', { params: { page, per_page: perPage } }).then(r => payrollPaginator(r.data)),

  explainMyPayrollException: (id: number, body: FormData): Promise<{ success: boolean; message: string }> =>
    employeeApiClient.post(`employee/payroll-exceptions/${id}/explain`, body, {
      headers: { 'Content-Type': undefined },
    }).then(r => r.data),

  getExportStatus: (jobId: string, signal?: AbortSignal): Promise<PayrollExportJobStatus> =>
    employeeApiClient.get(`employee/payroll/exports/${jobId}`, { signal }).then(r => payrollExportStatus(r.data)),
};

export default apiClient;
 
